<template>
  <div>
    <a-form class="ant-advanced-search-form" :form="depotSearchForm" @submit="handleDepotSearch">
      <a-row :gutter="24">
        <a-col :span="8">
          <a-form-item label="Company">
            <a-select v-decorator="['company']" :allowClear="true" placeholder="Select company">
              <a-select-option v-for="(item, index) in displayCompanies" :key="index" :value="item">
                {{ item }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <a-col :span="16" :style="{ textAlign: 'right' }">
          <a-form-item>
            <a-button type="primary" html-type="submit" icon="search">
              Search Depots
            </a-button>
            <a-button
              type="primary"
              @click="() => (this.createDepotModalVisible = true)"
              :style="{ marginLeft: '8px' }"
              icon="plus"
            >
              Create New Depot
            </a-button>
            <a-tooltip placement="top" title="Select depots from the same company to perform group actions.">
              <a-dropdown :trigger="['click']">
                <a-menu slot="overlay" @click="handleGroupActionClick">
                  <a-menu-item key="moveChargers" :disabled="moveChargersButtonDisabled">
                    <a-tooltip placement="top" title="Select two depots from the same company to perform this action."
                      ><a-icon type="swap" />Move Chargers
                    </a-tooltip></a-menu-item
                  >
                  <a-menu-divider />
                  <a-menu-item key="allowUnrestrictedCharging" :disabled="!hasSelectedMultiple">
                    <a-icon type="clock-circle" />Allow Unrestricted Charging
                  </a-menu-item>
                  <a-menu-item key="removeUnrestrictedCharging" :disabled="!hasSelectedMultiple">
                    <a-icon type="close" />Remove Unrestricted Charging
                  </a-menu-item>
                  <a-menu-item key="addTimeSlot" :disabled="!hasSelectedMultiple">
                    <a-icon type="plus" />Add Time Slot
                  </a-menu-item>
                </a-menu>
                <a-button style="margin-left: 8px" type="primary"> Group Actions <a-icon type="down" /> </a-button>
              </a-dropdown>
            </a-tooltip>
          </a-form-item>
        </a-col>
      </a-row>
    </a-form>
    <br />
    <div class="search-result-list">
      <a-table
        :columns="depotColumns"
        :dataSource="handleDepotData"
        :scroll="{ x: 1500, y: 1000 }"
        :loading="depotDataLoading"
        :rowKey="record => record.key"
        :pagination="{
          defaultPageSize: 10,
          showSizeChanger: true,
          position: 'both'
        }"
        bordered
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
      >
        <span slot="serverSyncStatus" slot-scope="depotRecord">
          <span v-if="depotRecord.syncStatus === 'true'">
            <a-badge status="success">
              <a-tooltip title="All settings synced!">Synced</a-tooltip>
            </a-badge>
          </span>
          <span v-else-if="depotRecord.syncStatus === 'false'">
            <a-badge status="error">
              <a-tooltip title="Click to sync!">
                <a @click="showResyncConfirm(depotRecord)">Not Synced</a>
              </a-tooltip>
            </a-badge>
          </span>
          <span v-else-if="depotRecord.syncStatus === 'error'">
            <a-badge status="warning">
              <a-tooltip title="Click to retry!">
                <a @click="getSingleSyncStatus(depotRecord)">Error Retrieving Status</a>
              </a-tooltip>
            </a-badge>
          </span>
          <span v-else>
            <a-badge status="default">
              <a-tooltip title="IoT connection closed abruptly!">Cannot Retrieve Status</a-tooltip>
            </a-badge>
          </span>
        </span>
        <span slot="actions" slot-scope="depotRecord">
          <a
            @click="
              () => {
                addChargerFormInitialValues = depotRecord;
                addChargerModalVisible = true;
              }
            "
            >Add Chargers</a
          >
          <a-divider type="vertical" />
          <a
            @click="
              () => {
                editConfigurationFormInitialValues = depotRecord;
                fleetList = masterCompanyMap.get(depotRecord.company).fleets;
                editConfigurationModalVisible = true;
              }
            "
            >View Configuration</a
          >
          <a-divider type="vertical" />
          <a-dropdown :trigger="['click']">
            <a class="ant-dropdown-link" @click="e => e.preventDefault()"> More <a-icon type="down" /> </a>
            <a-menu slot="overlay">
              <a-menu-item key="1">
                <a
                  @click="
                    () => {
                      chargingScheduleIntitialValues = depotRecord;
                      let timeSlots = JSON.parse(depotRecord.timeSlots);
                      timeSlots.forEach((timeSlot, index) => {
                        timeSlot.rowKey = index;
                      });
                      chargingScheduleDataSource = timeSlots;
                      allowUnrestrictedCharging = checkZeroTimeSlot(chargingScheduleDataSource);
                      chargingScheduleModalVisible = true;
                    }
                  "
                  >View Charging Restriction Schedule</a
                >
              </a-menu-item>
              <a-menu-item key="2">
                <a
                  @click="
                    () => {
                      userListInitialValues = depotRecord;
                      let userList = JSON.parse(depotRecord.userList);
                      userList.forEach((user, index) => {
                        user.rowKey = index;
                      });
                      userListDataSource = userList;
                      userListModalVisible = true;
                    }
                  "
                  >View User List</a
                >
              </a-menu-item>
              <a-menu-item key="3" v-if="depotRecord.isActive === true">
                <a @click="showUpdateIsActiveConfirm(depotRecord, false)">Disable Depot</a>
              </a-menu-item>
              <a-menu-item key="4" v-else>
                <a @click="showUpdateIsActiveConfirm(depotRecord, true)">Enable Depot</a>
              </a-menu-item>
              <a-menu-item key="5">
                <a @click="showResyncConfirm(depotRecord, true)">Resync Server</a>
              </a-menu-item>
            </a-menu>
          </a-dropdown>
        </span>
        <template slot="expandedRowRender" slot-scope="depotRecord">
          <a-table
            :columns="innerDepotColumns"
            :dataSource="
              [...depotRecord.chargerList].sort(
                (recordOne, recordTwo) => recordOne.chargerNumber - recordTwo.chargerNumber
              )
            "
            :pagination="false"
            :rowKey="chargerRecord => chargerRecord.entityId"
          >
            <span slot="actions" slot-scope="chargerRecord">
              <a-popconfirm ok-text="Yes" cancel-text="No" @confirm="deleteCharger(depotRecord, chargerRecord)">
                <template slot="title">
                  <p style="margin:0">
                    Are you sure you want to
                    <span style="color:#f50">delete</span> this charger?
                  </p>
                </template>
                <a>Delete</a>
              </a-popconfirm>
              <a-divider type="vertical" />
              <a
                @click="
                  () => {
                    editChargerFormInitialValues = depotRecord;
                    editChargerFormInitialValues.selectedChargerKey = chargerRecord.key;
                    editChargerFormInitialValues.selectedChargerNumber = chargerRecord.chargerNumber;
                    editChargerFormInitialValues.selectedMacID = chargerRecord.macID;
                    editChargerFormInitialValues.selectedPriority = chargerRecord.priority;
                    editChargerModalVisible = true;
                  }
                "
                >Edit</a
              >
            </span>
          </a-table>
        </template>
        <span slot="footer">
          <span style="float: left; display: block;">
            {{ this.depotEntriesFound + " Depot(s) Found" }}
          </span>
          <span style="text-align: right; display: block;">
            {{ this.totalNumberOfChargers + " Total Chargers" }}
          </span>
        </span>
      </a-table>
      <a-modal
        v-model="addChargerModalVisible"
        title="Add Chargers"
        centered
        :afterClose="
          () => {
            this.addChargerForm.resetFields();
            this.uploadFileList = [];
            this.uploadCSVButtonDisabled = false;
          }
        "
      >
        <a-form layout="vertical" id="addChargerForm" :form="addChargerForm" @submit="addChargers">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Depot Name">
                <a-input
                  v-decorator="[
                    'depotName',
                    {
                      initialValue: addChargerFormInitialValues.depot
                    }
                  ]"
                  :disabled="true"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <span slot="label"
                  >Chargers to Add
                  <a-popover title="Charger List Format">
                    <template slot="content">
                      <p>Please provide the list of chargers as a JSON Array. For example:</p>
                      <p>[ { "chargerNumber" : 1, "macID" : "000123456789", "priority" : 1 } ]</p>
                      <p><b>Required Attributes:</b> chargerNumber, macID, priority</p>
                      <p>
                        <b>TIP:</b> Convert CSV to JSON
                        <a href="https://csvjson.com/csv2json" target="_blank" rel="noopener noreferrer">
                          here.
                        </a>
                      </p>
                    </template>
                    <a-icon type="question-circle-o" />
                  </a-popover>
                </span>
                <a-textarea
                  allowClear
                  placeholder="JSON Charger List"
                  :auto-size="{ minRows: 5, maxRows: 10 }"
                  v-decorator="[
                    'chargerList',
                    {
                      rules: [
                        {
                          validator: customAddFormValidator
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item style="text-align: center">
                <a-upload
                  accept=".csv"
                  :headers="headers"
                  :customRequest="dummyUploadRequest"
                  @change="handleUploadChange"
                  :fileList="uploadFileList"
                  :multiple="false"
                >
                  <a-button icon="upload" :disabled="uploadCSVButtonDisabled">Upload CSV</a-button>
                </a-upload>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item style="text-align: center">
                <a-button
                  icon="plus"
                  @click="
                    () => {
                      addIndividualChargerModalVisible = true;
                    }
                  "
                  >Add Single Charger</a-button
                >
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="addChargerForm" type="primary" html-type="submit" :loading="addChargerFormButtonLoading">
            Add Chargers
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="editChargerModalVisible"
        title="Edit Charger Record"
        centered
        :afterClose="
          () => {
            editChargerForm.resetFields();
            editChargerFormInitialValues = {};
          }
        "
      >
        <a-form layout="vertical" id="editChargerForm" :form="editChargerForm" @submit="editCharger">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Depot Name">
                <a-input
                  v-decorator="[
                    'depotName',
                    {
                      initialValue: editChargerFormInitialValues.depot
                    }
                  ]"
                  :disabled="true"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Charger Number">
                <a-input-number
                  v-decorator="[
                    'chargerNumber',
                    {
                      rules: [
                        {
                          validator: customEditFormValidator
                        }
                      ],
                      initialValue: editChargerFormInitialValues.selectedChargerNumber
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Priority">
                <a-input-number
                  v-decorator="[
                    'priority',
                    {
                      rules: [
                        {
                          validator: customEditFormValidator
                        }
                      ],
                      initialValue: editChargerFormInitialValues.selectedPriority
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="MAC ID">
                <a-input
                  v-decorator="[
                    'macID',
                    {
                      rules: [
                        {
                          validator: customEditFormValidator
                        }
                      ],
                      initialValue: editChargerFormInitialValues.selectedMacID
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button
            form="editChargerForm"
            type="primary"
            key="saveChanges"
            html-type="submit"
            :loading="editChargerFormButtonLoading"
          >
            Save Changes
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="createDepotModalVisible"
        title="Create New Depot"
        centered
        :maskClosable="false"
        :afterClose="
          () => {
            this.createDepotForm.resetFields();
            this.uploadFileList = [];
            this.fleetList = [];
            this.configUploadFileList = [];
            this.uploadCSVButtonDisabled = false;
            this.uploadTXTButtonDisabled = false;
            this.createDepotLoadingPercentage = 0;
          }
        "
        id="backModal"
      >
        <a-form layout="vertical" id="createDepotForm" :form="createDepotForm" @submit="createDepot">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Depot Name">
                <a-input
                  v-decorator="[
                    'depot',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a depot name!'
                        },
                        {
                          validator: customDepotNameValidator
                        }
                      ]
                    }
                  ]"
                  placeholder="Depot"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Company">
                <a-select
                  v-decorator="[
                    'company',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select a company!'
                        }
                      ]
                    }
                  ]"
                  :allowClear="true"
                  placeholder="Select company"
                  @change="handleCompanySelectChange"
                >
                  <a-select-option v-for="(item, index) in displayCompanies" :key="index" :value="item">
                    {{ item }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Fleet(s)">
                <a-select
                  v-decorator="[
                    'fleetList',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select a fleet!'
                        },
                        {
                          validator: customFleetListValidator
                        }
                      ]
                    }
                  ]"
                  :allowClear="true"
                  placeholder="Select fleet"
                  mode="multiple"
                  :getPopupContainer="trigger => trigger.parentNode"
                >
                  <a-select-option v-for="(item, index) in fleetList" :key="index" :value="item">
                    {{ item }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Maximum SOC To Charge (%)">
                <a-input-number
                  v-decorator="[
                    'maxSocToCharge',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Maximum Concurrent Chargers">
                <a-input-number
                  v-decorator="[
                    'maxConcurrentChargers',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Minimum SOC For Pause (%)">
                <a-input-number
                  v-decorator="[
                    'minimumSocRequiredForPauseCommand',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Maximum Charging Current (amp)">
                <a-input-number
                  v-decorator="[
                    'maxChargingCurrent',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Timeout For Charging Disconnect (s)">
                <a-input-number
                  v-decorator="[
                    'timeoutForChargingDisconnect',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Timeout For Ready Disconnect (s)">
                <a-input-number
                  v-decorator="[
                    'timeoutForReadyDisconnect',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="SOC Threshold High (%)">
                <a-input-number
                  v-decorator="[
                    'SOCThresholdHigh',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="SOC Threshold Median (%)">
                <a-input-number
                  v-decorator="[
                    'SOCThresholdMedian',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="UTC Hour Offset (+/-)">
                <a-input-number
                  v-decorator="[
                    'utcHoursOffset',
                    {
                      initialValue: 0
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Gateway Program Version">
                <a-input
                  style="width: 40%"
                  v-decorator="[
                    'programVersion',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a value!'
                        }
                      ],
                      initialValue: '2.0.0'
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Timeout For Disconnect Alert (min)">
                <a-input-number
                  v-decorator="[
                    'timeoutForDisconnectAlert',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Count Overridden Charger">
                <a-radio-group
                  v-decorator="[
                    'overriddenChargerCount',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue: 'true'
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Send Alert Emails">
                <a-radio-group
                  v-decorator="[
                    'sendSNSAlert',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue: 'true'
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Client Endpoint">
                <a-input
                  v-decorator="[
                    'clientEndpoint',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter the client endpoint!'
                        }
                      ]
                    }
                  ]"
                  placeholder="<endpoint_id>-ats.iot.<region>.amazonaws.com"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Private Key File">
                <a-input
                  v-decorator="[
                    'privateKeyFile',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a private key file name!'
                        }
                      ]
                    }
                  ]"
                  placeholder="cert/<certificate_id>-private.pem.key"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Certificate File">
                <a-input
                  v-decorator="[
                    'certificateFile',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a certificate file name!'
                        }
                      ]
                    }
                  ]"
                  placeholder="cert/<certificate_id>-certificate.pem.crt"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <span slot="label"
                  >User List
                  <a-popover title="User List Format">
                    <template slot="content">
                      <p>
                        Please provide the list of authorized users for the client program as a JSON Array. For example:
                      </p>
                      <p>[ { "username" : "John Smith", "pin" : "1234" } ]</p>
                      <p><b>Required Attributes:</b> username, pin</p>
                      <p>
                        <b>TIP:</b> Convert CSV to JSON
                        <a href="https://csvjson.com/csv2json" target="_blank" rel="noopener noreferrer">
                          here.
                        </a>
                      </p>
                    </template>
                    <a-icon type="question-circle-o" />
                  </a-popover>
                </span>
                <a-textarea
                  allowClear
                  placeholder="JSON User List"
                  :auto-size="{ minRows: 3, maxRows: 5 }"
                  v-decorator="[
                    'userList',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a list of users!'
                        },
                        {
                          validator: customCreateDepotFormValidator
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item style="text-align: center">
                <a-button
                  size="small"
                  icon="plus"
                  @click="
                    () => {
                      addUserModalVisible = true;
                    }
                  "
                  >Add User</a-button
                >
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <span slot="label"
                  >No Charging Time Slots
                  <a-popover title="Time Slots Format">
                    <template slot="content">
                      <p>Please provide the list of time slots as a JSON Array. For example:</p>
                      <p>
                        [ { "noChargingStopTime" : "02:15", "isWeekendExcluded" : true, "isOvernight" : true,
                        "noChargingStartTime" : "21:45" } ]
                      </p>
                      <p><b>Required Attributes:</b> noChargingStartTime, noChargingStopTime, isOvernight</p>
                      <p>
                        <b>TIP:</b> Convert CSV to JSON
                        <a href="https://csvjson.com/csv2json" target="_blank" rel="noopener noreferrer">
                          here.
                        </a>
                      </p>
                    </template>
                    <a-icon type="question-circle-o" />
                  </a-popover>
                </span>
                <a-textarea
                  allowClear
                  placeholder="JSON Time Slots"
                  :auto-size="{ minRows: 3, maxRows: 5 }"
                  v-decorator="[
                    'timeSlots',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a list of time slots!'
                        },
                        {
                          validator: customCreateDepotFormValidator
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item style="text-align: center">
                <a-button
                  icon="plus"
                  size="small"
                  @click="
                    () => {
                      addTimeSlotModalVisible = true;
                    }
                  "
                  >Add Time Slot</a-button
                >
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <span slot="label"
                  >Charger List
                  <a-popover title="Charger List Format">
                    <template slot="content">
                      <p>Please provide the list of chargers as a JSON Array. For example:</p>
                      <p>[ { "chargerNumber" : 1, "macID" : "000123456789", "priority" : 1 } ]</p>
                      <p><b>Required Attributes:</b> chargerNumber, macID, priority</p>
                      <p>
                        <b>TIP:</b> Convert CSV to JSON
                        <a href="https://csvjson.com/csv2json" target="_blank" rel="noopener noreferrer">
                          here.
                        </a>
                      </p>
                    </template>
                    <a-icon type="question-circle-o" />
                  </a-popover>
                </span>
                <a-textarea
                  allowClear
                  placeholder="JSON Charger List"
                  :auto-size="{ minRows: 5, maxRows: 10 }"
                  v-decorator="[
                    'chargerList',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a list of chargers!'
                        },
                        {
                          validator: customCreateDepotFormValidator
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item style="text-align: center">
                <a-upload
                  accept=".csv"
                  :headers="headers"
                  :customRequest="dummyUploadRequest"
                  @change="handleUploadChange"
                  :fileList="uploadFileList"
                  :multiple="false"
                >
                  <a-button size="small" icon="upload" :disabled="uploadCSVButtonDisabled">Charger List CSV</a-button>
                </a-upload>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <span slot="label"
                  >Authorize Users
                  <a-tooltip title="Select users to give them access to the new depot.">
                    <a-icon type="question-circle-o" />
                  </a-tooltip>
                </span>
                <a-tree-select
                  v-decorator="['authorizedUsers']"
                  :allowClear="true"
                  placeholder="Select Users"
                  :dropdown-style="{ maxHeight: '300px', overflow: 'auto' }"
                  style="width: 100%"
                  :tree-data="userTreeData"
                  tree-checkable
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item style="text-align: center">
                <a-upload
                  accept=".txt"
                  :headers="headers"
                  :customRequest="dummyUploadRequest"
                  @change="handleConfigurationUpload"
                  :fileList="configUploadFileList"
                  :multiple="false"
                >
                  <a-button icon="upload" :disabled="uploadTXTButtonDisabled">Upload Configuration File</a-button>
                </a-upload>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-row>
            <a-col :span="16">
              <a-progress :percent="createDepotLoadingPercentage" size="small" status="active" />
            </a-col>
            <a-col :span="8">
              <a-button
                form="createDepotForm"
                type="primary"
                html-type="submit"
                :loading="createDepotFormButtonLoading"
              >
                Create Depot
              </a-button>
            </a-col>
          </a-row>
        </template>
      </a-modal>
      <a-modal
        v-model="editConfigurationModalVisible"
        title="Configuration Settings"
        centered
        :afterClose="
          () => {
            this.editConfigurationForm.resetFields();
            this.editingDisabled = true;
            this.editConfigurationFormInitialValues = {};
            this.fleetList = [];
          }
        "
      >
        <a-form id="editConfigurationForm" :form="editConfigurationForm" @submit="saveConfiguration">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Depot Name">
                <a-input
                  v-decorator="[
                    'depot',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a depot name!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.depot
                    }
                  ]"
                  :disabled="true"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Fleet(s)">
                <a-select
                  v-decorator="[
                    'fleetList',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one fleet!'
                        },
                        {
                          validator: customFleetListValidator
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.fleetList
                        ? JSON.parse(editConfigurationFormInitialValues.fleetList)
                        : ''
                    }
                  ]"
                  :allowClear="true"
                  :disabled="editingDisabled"
                  mode="multiple"
                  :getPopupContainer="trigger => trigger.parentNode"
                >
                  <a-select-option v-for="(item, index) in fleetList" :key="index" :value="item">
                    {{ item }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Maximum SOC To Charge (%)">
                <a-input-number
                  v-decorator="[
                    'maxSocToCharge',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.maxSocToCharge
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Maximum Concurrent Chargers">
                <a-input-number
                  v-decorator="[
                    'maxConcurrentChargers',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.maxConcurrentChargers
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Minimum SOC For Pause (%)">
                <a-input-number
                  v-decorator="[
                    'minimumSocRequiredForPauseCommand',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.minimumSocRequiredForPauseCommand
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Maximum Charging Current (amp)">
                <a-input-number
                  v-decorator="[
                    'maxChargingCurrent',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.maxChargingCurrent
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Timeout For Charging Disconnect (s)">
                <a-input-number
                  v-decorator="[
                    'timeoutForChargingDisconnect',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.timeoutForChargingDisconnect
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Timeout For Ready Disconnect (s)">
                <a-input-number
                  v-decorator="[
                    'timeoutForReadyDisconnect',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.timeoutForReadyDisconnect
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="SOC Threshold High (%)">
                <a-input-number
                  v-decorator="[
                    'SOCThresholdHigh',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.SOCThresholdHigh
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="SOC Threshold Median (%)">
                <a-input-number
                  v-decorator="[
                    'SOCThresholdMedian',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          max: 100,
                          message: 'Value must be a valid number between [1-100]'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.SOCThresholdMedian
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="UTC Hour Offset (+/-)">
                <a-input-number
                  v-decorator="[
                    'utcHoursOffset',
                    {
                      initialValue: editConfigurationFormInitialValues.utcHoursOffset
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Timeout For Disconnect Alert (min)">
                <a-input-number
                  v-decorator="[
                    'timeoutForDisconnectAlert',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a number!'
                        },
                        {
                          type: 'number',
                          min: 1,
                          message: 'Value must be a non-zero positive number!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.timeoutForDisconnectAlert
                    }
                  ]"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Send Alert Emails">
                <a-radio-group
                  v-decorator="[
                    'sendSNSAlert',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue:
                        editConfigurationFormInitialValues.sendSNSAlert != null
                          ? editConfigurationFormInitialValues.sendSNSAlert.toString()
                          : null
                    }
                  ]"
                  :disabled="editingDisabled"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Count Overridden Charger">
                <a-radio-group
                  v-decorator="[
                    'overriddenChargerCount',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue:
                        editConfigurationFormInitialValues.overriddenChargerCount != null
                          ? editConfigurationFormInitialValues.overriddenChargerCount.toString()
                          : null
                    }
                  ]"
                  :disabled="editingDisabled"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Client Endpoint">
                <a-input
                  v-decorator="[
                    'clientEndpoint',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter the client endpoint!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.clientEndpoint
                    }
                  ]"
                  placeholder="<endpoint_id>-ats.iot.<region>.amazonaws.com"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Private Key File">
                <a-input
                  v-decorator="[
                    'privateKeyFile',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a private key file name!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.privateKeyFile
                    }
                  ]"
                  placeholder="cert/<certificate_id>-private.pem.key"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item label="Certificate File">
                <a-input
                  v-decorator="[
                    'certificateFile',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a certificate file name!'
                        }
                      ],
                      initialValue: editConfigurationFormInitialValues.certificateFile
                    }
                  ]"
                  placeholder="cert/<certificate_id>-certificate.pem.crt"
                  :disabled="editingDisabled"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button @click="() => (this.editingDisabled = false)">
            Edit
          </a-button>
          <a-button
            form="editConfigurationForm"
            type="primary"
            html-type="submit"
            :loading="editConfigurationFormButtonLoading"
            :disabled="editingDisabled"
          >
            Save Changes
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="chargingScheduleModalVisible"
        title="Charging Restriction Schedule"
        centered
        :afterClose="
          () => {
            this.chargingScheduleForm.resetFields();
            this.chargingScheduleIntitialValues = {};
            this.chargingScheduleDataSource = [];
          }
        "
        width="700"
        id="backModal"
      >
        <a-form id="chargingScheduleForm" :form="chargingScheduleForm" @submit="saveChargingSchedule">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <a-table
                  :columns="chargingScheduleColumns"
                  :dataSource="chargingScheduleDataSource"
                  :pagination="false"
                  :rowKey="(record, index) => index + 1"
                  bordered
                >
                  <span slot="actions" slot-scope="timeSlot">
                    <a-popconfirm title="Sure to delete?" @confirm="() => deleteTimeSlot(timeSlot.rowKey)">
                      <a>Delete</a>
                    </a-popconfirm>
                    <a-divider type="vertical" />
                    <a
                      @click="
                        () => {
                          editTimeSlotFormInitialValues = timeSlot;
                          editTimeSlotModalVisible = true;
                        }
                      "
                      >Edit</a
                    >
                  </span>
                </a-table>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24" style="text-align: center">
            <a-col :span="12">
              <a-form-item>
                <a-button
                  icon="plus"
                  @click="
                    () => {
                      addTimeSlotModalVisible = true;
                    }
                  "
                  >Add Time Slot</a-button
                >
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item>
                <a-checkbox
                  v-decorator="['allowUnrestrictedCharging']"
                  :checked="allowUnrestrictedCharging"
                  @change="handleCheckboxChange"
                >
                  Allow Unrestricted Charging
                  <a-tooltip title="Selecting this will disregard all present restrictions and allow charging 24/7.">
                    <a-icon type="question-circle-o" />
                  </a-tooltip>
                </a-checkbox>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button
            form="chargingScheduleForm"
            type="primary"
            html-type="submit"
            :loading="chargingScheduleButtonLoading"
          >
            Save Changes
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="userListModalVisible"
        title="Client Program User List"
        centered
        :afterClose="
          () => {
            this.userListForm.resetFields();
            this.userListIntitialValues = {};
            this.userListDataSource = [];
          }
        "
        :width="600"
        id="backModal"
      >
        <a-form id="userListForm" :form="userListForm" @submit="saveUserList">
          <a-row :gutter="24">
            <a-col :span="24">
              <a-form-item>
                <a-table
                  :columns="userListColumns"
                  :dataSource="userListDataSource"
                  :pagination="false"
                  :rowKey="(record, index) => index + 1"
                  bordered
                >
                  <span slot="actions" slot-scope="user">
                    <a-popconfirm title="Sure to delete?" @confirm="() => deleteUser(user.rowKey)">
                      <a>Delete</a>
                    </a-popconfirm>
                  </span>
                </a-table>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24" style="text-align: center">
            <a-col :span="24">
              <a-form-item>
                <a-button
                  icon="plus"
                  @click="
                    () => {
                      addUserModalVisible = true;
                    }
                  "
                  >Add User</a-button
                >
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="userListForm" type="primary" html-type="submit" :loading="userListButtonLoading">
            Save User List
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="addIndividualChargerModalVisible"
        title="Add Single Charger"
        centered
        :afterClose="
          () => {
            this.addIndividualChargerForm.resetFields();
          }
        "
      >
        <a-form id="addIndividualChargerForm" :form="addIndividualChargerForm" @submit="addIndividualCharger">
          <a-row :gutter="24">
            <a-col :span="8">
              <a-form-item label="Charger Number">
                <a-input-number
                  v-decorator="[
                    'chargerNumber',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a charger number!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="MAC ID">
                <a-input
                  v-decorator="[
                    'macID',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a MAC ID!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="Priority">
                <a-input-number
                  v-decorator="[
                    'priority',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a priority!'
                        }
                      ]
                    }
                  ]"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="addIndividualChargerForm" type="primary" html-type="submit">
            Add Charger
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="addUserModalVisible"
        title="Add Authorized User"
        centered
        :afterClose="
          () => {
            this.addUserForm.resetFields();
          }
        "
        id="addUser"
      >
        <a-form id="addUserForm" :form="addUserForm" @submit="addUser">
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Username">
                <a-input
                  v-decorator="[
                    'username',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a username!'
                        }
                      ]
                    }
                  ]"
                  placeholder="username"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Pin">
                <a-input
                  v-decorator="[
                    'pin',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a pin!'
                        },
                        {
                          len: 4,
                          message: 'Pin must be exactly 4 digits!'
                        }
                      ]
                    }
                  ]"
                  placeholder="4-digit pin"
                />
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="addUserForm" type="primary" html-type="submit">
            Add User
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="addTimeSlotModalVisible"
        title="Add No Charging Time Slot"
        centered
        :afterClose="
          () => {
            this.addTimeSlotForm.resetFields();
            this.addTimeSlotFormButtonLoading = false;
          }
        "
        id="addTimeSlot"
      >
        <a-form id="addTimeSlotForm" :form="addTimeSlotForm" @submit="addTimeSlot">
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="No Charging Start Time">
                <a-time-picker
                  v-decorator="[
                    'noChargingStartTime',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a time!'
                        }
                      ],
                      initialValue: this.$moment('12:15', 'HH:mm')
                    }
                  ]"
                  format="HH:mm"
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="No Charging Stop Time">
                <a-time-picker
                  v-decorator="[
                    'noChargingStopTime',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a time!'
                        }
                      ],
                      initialValue: this.$moment('15:15', 'HH:mm')
                    }
                  ]"
                  format="HH:mm"
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Is Weekend Excluded">
                <a-radio-group
                  v-decorator="[
                    'isWeekendExcluded',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue: 'false'
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Is Overnight">
                <a-radio-group
                  v-decorator="[
                    'isOvernight',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue: 'false'
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="addTimeSlotForm" type="primary" html-type="submit" :loading="addTimeSlotFormButtonLoading">
            Add Time Slot
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="editTimeSlotModalVisible"
        title="Edit No Charging Time Slot"
        centered
        :afterClose="
          () => {
            this.editTimeSlotForm.resetFields();
          }
        "
        id="editTimeSlot"
      >
        <a-form id="editTimeSlotForm" :form="editTimeSlotForm" @submit="editTimeSlot">
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="No Charging Start Time">
                <a-time-picker
                  v-decorator="[
                    'noChargingStartTime',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a time!'
                        }
                      ],
                      initialValue: this.$moment(editTimeSlotFormInitialValues.noChargingStartTime, 'HH:mm')
                    }
                  ]"
                  format="HH:mm"
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="No Charging Stop Time">
                <a-time-picker
                  v-decorator="[
                    'noChargingStopTime',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please enter a time!'
                        }
                      ],
                      initialValue: this.$moment(editTimeSlotFormInitialValues.noChargingStopTime, 'HH:mm')
                    }
                  ]"
                  format="HH:mm"
                  :getPopupContainer="trigger => trigger.parentNode"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24">
            <a-col :span="12">
              <a-form-item label="Is Weekend Excluded">
                <a-radio-group
                  v-decorator="[
                    'isWeekendExcluded',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue:
                        editTimeSlotFormInitialValues.isWeekendExcluded != null
                          ? editTimeSlotFormInitialValues.isWeekendExcluded.toString()
                          : null
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="Is Overnight">
                <a-radio-group
                  v-decorator="[
                    'isOvernight',
                    {
                      rules: [
                        {
                          required: true,
                          message: 'Please select at least one choice!'
                        }
                      ],
                      initialValue:
                        editTimeSlotFormInitialValues.isOvernight != null
                          ? editTimeSlotFormInitialValues.isOvernight.toString()
                          : null
                    }
                  ]"
                >
                  <a-radio value="true">
                    Yes
                  </a-radio>
                  <a-radio value="false">
                    No
                  </a-radio>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>
        </a-form>
        <template slot="footer">
          <a-button form="editTimeSlotForm" type="primary" html-type="submit">
            Save Time Slot
          </a-button>
        </template>
      </a-modal>
      <a-modal
        v-model="moveChargersModalVisible"
        title="Move Chargers"
        centered
        width="700"
        :afterClose="
          () => {
            this.moveChargersTitles = [];
            this.moveChargersData = [];
            this.moveChargersTargetKeys = [];
            this.moveChargersCacheData = [];
            this.moveChargersForm.resetFields();
            this.editingKey = '';
            this.moveChargersLoadingPercentage = 0;
          }
        "
      >
        <a-form id="moveChargersForm" :form="moveChargersForm" @submit="saveMoveChargers">
          <a-form-item>
            <a-transfer
              :dataSource="moveChargersData"
              :targetKeys="moveChargersTargetKeys"
              :titles="moveChargersTitles"
              @change="handleMoveChargersChange"
              v-decorator="['transferTable']"
              :showSelectAll="false"
            >
              <template
                slot="children"
                slot-scope="{ props: { filteredItems, selectedKeys }, on: { itemSelectAll, itemSelect } }"
              >
                <a-table
                  :row-selection="getRowSelection({ selectedKeys, itemSelectAll, itemSelect })"
                  :columns="moveChargerColumns"
                  :dataSource="filteredItems"
                  bordered
                >
                  <template
                    v-for="columnName in ['chargerNumber', 'priority']"
                    :slot="columnName"
                    slot-scope="text, record"
                  >
                    <div :key="columnName">
                      <a-input-number
                        v-if="record.editable"
                        style="margin: -5px 0"
                        :value="text"
                        @change="value => handleChargerRecordChange(value, record.key, columnName)"
                      />
                      <template v-else>
                        {{ text }}
                      </template>
                    </div>
                  </template>
                  <template slot="actions" slot-scope="text, record">
                    <span v-if="record.editable">
                      <a @click="() => saveChargerRecord(record.key)">Save</a>
                      <a-divider type="vertical" />
                      <a-popconfirm title="Sure to cancel?" @confirm="() => cancelChargerRecordChange(record.key)">
                        <a>Cancel</a>
                      </a-popconfirm>
                    </span>
                    <span v-else>
                      <a :disabled="editingKey !== ''" @click="() => editChargerRecord(record.key)">Edit</a>
                    </span>
                  </template>
                </a-table>
              </template>
            </a-transfer>
          </a-form-item>
        </a-form>
        <template slot="footer">
          <a-row>
            <a-col :span="16">
              <a-progress :percent="moveChargersLoadingPercentage" status="active" />
            </a-col>
            <a-col :span="8">
              <a-button
                form="moveChargersForm"
                :loading="moveChargersFormButtonLoading"
                type="primary"
                html-type="submit"
                :disabled="editingKey !== ''"
              >
                Save Changes
              </a-button>
            </a-col>
          </a-row>
        </template>
      </a-modal>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { difference, cloneDeep } from "lodash";

export default {
  data() {
    return {
      depotSearchForm: this.$form.createForm(this),
      editChargerForm: this.$form.createForm(this),
      addChargerForm: this.$form.createForm(this),
      addTimeSlotForm: this.$form.createForm(this),
      addUserForm: this.$form.createForm(this),
      editTimeSlotForm: this.$form.createForm(this),
      createDepotForm: this.$form.createForm(this),
      editConfigurationForm: this.$form.createForm(this),
      moveChargersForm: this.$form.createForm(this),
      addIndividualChargerForm: this.$form.createForm(this),
      chargingScheduleForm: this.$form.createForm(this),
      userListForm: this.$form.createForm(this),
      depotData: [],
      userData: [],
      chargingScheduleDataSource: [],
      userListDataSource: [],
      uploadFileList: [],
      fleetList: [],
      configUploadFileList: [],
      selectedRowKeys: [],
      selectedRows: [],
      moveChargersData: [],
      moveChargersCacheData: [],
      moveChargersTargetKeys: [],
      moveChargersTitles: [],
      editChargerFormInitialValues: {},
      addChargerFormInitialValues: {},
      editTimeSlotFormInitialValues: {},
      editConfigurationFormInitialValues: {},
      chargingScheduleIntitialValues: {},
      userListInitialValues: {},
      depotDataLoading: false,
      editChargerModalVisible: false,
      editChargerFormButtonLoading: false,
      createDepotModalVisible: false,
      createDepotFormButtonLoading: false,
      addChargerModalVisible: false,
      addIndividualChargerModalVisible: false,
      addTimeSlotModalVisible: false,
      addTimeSlotFormButtonLoading: false,
      addUserModalVisible: false,
      editTimeSlotModalVisible: false,
      addChargerFormButtonLoading: false,
      editConfigurationModalVisible: false,
      editConfigurationFormButtonLoading: false,
      moveChargersModalVisible: false,
      moveChargersFormButtonLoading: false,
      chargingScheduleModalVisible: false,
      chargingScheduleButtonLoading: false,
      userListModalVisible: false,
      userListButtonLoading: false,
      editingDisabled: true,
      allowUnrestrictedCharging: false,
      uploadCSVButtonDisabled: false,
      uploadTXTButtonDisabled: false,
      depotEntriesFound: 0,
      totalNumberOfChargers: 0,
      createDepotLoadingPercentage: 0,
      moveChargersLoadingPercentage: 0,
      editingKey: "",
      headers: {
        authorization: "authorization-text"
      },
      depotColumns: [
        {
          dataIndex: "depot",
          title: "Depot Name",
          align: "center",
          width: 220
        },
        {
          dataIndex: "company",
          title: "Company",
          align: "center"
        },
        {
          dataIndex: "numberOfChargers",
          title: "Number of Chargers",
          align: "center"
        },
        {
          title: "Is Active",
          dataIndex: "isActive",
          align: "center",
          customRender: isActive => {
            if (isActive) {
              return "Yes";
            } else {
              return <p style="color:#f50; margin:0">No</p>;
            }
          },
          filters: [
            {
              text: "Yes",
              value: "true"
            },
            {
              text: "No",
              value: "false"
            }
          ],
          filterMultiple: false,
          onFilter: (value, record) => record.isActive.toString() === value
        },
        {
          title: () => {
            return (
              <span>
                Server Sync Status&nbsp;
                <a-tooltip title="Displays whether the server program's configuration settings are synced with the database.">
                  <a-icon type="info-circle" />
                </a-tooltip>
              </span>
            );
          },
          align: "center",
          scopedSlots: { customRender: "serverSyncStatus" },
          width: 200
        },
        {
          title: "Last Updated",
          dataIndex: "revision",
          customRender: revision => {
            if (revision) {
              return this.$moment(revision)
                .local()
                .format("YYYY-MM-DD HH:mm");
            } else {
              return "N/A";
            }
          },
          align: "center"
        },
        {
          title: "Updated By",
          dataIndex: "updatedBy",
          align: "center"
        },
        {
          title: "Actions",
          scopedSlots: { customRender: "actions" },
          align: "center",
          width: 250
        }
      ],
      innerDepotColumns: [
        {
          dataIndex: "chargerNumber",
          title: "Charger Number",
          align: "center",
          sorter: (recordOne, recordTwo) => recordOne.chargerNumber - recordTwo.chargerNumber
        },
        {
          dataIndex: "macID",
          title: "MAC Address",
          align: "center",
          sorter: (recordOne, recordTwo) => recordOne.macID.localeCompare(recordTwo.macID)
        },
        {
          dataIndex: "priority",
          title: "Priority",
          align: "center",
          sorter: (recordOne, recordTwo) => recordOne.priority - recordTwo.priority
        },
        {
          title: "Last Updated",
          dataIndex: "revision",
          align: "center"
        },
        {
          title: "Actions",
          scopedSlots: { customRender: "actions" },
          align: "center"
        }
      ],
      chargingScheduleColumns: [
        {
          dataIndex: "noChargingStartTime",
          title: "No Charging Start Time",
          align: "center"
        },
        {
          dataIndex: "noChargingStopTime",
          title: "No Charging Stop Time",
          align: "center"
        },
        {
          dataIndex: "isOvernight",
          title: "Is Overnight",
          align: "center",
          customRender: isOvernight => {
            if (isOvernight) {
              return "Yes";
            } else {
              return "No";
            }
          }
        },
        {
          dataIndex: "isWeekendExcluded",
          title: "Is Weekend Excluded",
          align: "center",
          customRender: isWeekendExcluded => {
            if (isWeekendExcluded) {
              return "Yes";
            } else {
              return "No";
            }
          }
        },
        {
          title: "Actions",
          scopedSlots: { customRender: "actions" },
          align: "center"
        }
      ],
      userListColumns: [
        {
          dataIndex: "username",
          title: "Username",
          align: "center"
        },
        {
          dataIndex: "pin",
          title: "Pin",
          align: "center"
        },
        {
          title: "Actions",
          scopedSlots: { customRender: "actions" },
          align: "center"
        }
      ],
      moveChargerColumns: [
        {
          dataIndex: "chargerNumber",
          title: "Charger Number",
          align: "center",
          scopedSlots: { customRender: "chargerNumber" }
        },
        {
          dataIndex: "macID",
          title: "MAC Address",
          align: "center"
        },
        {
          dataIndex: "priority",
          title: "Priority",
          align: "center",
          scopedSlots: { customRender: "priority" }
        },
        {
          title: "Actions",
          align: "center",
          scopedSlots: { customRender: "actions" }
        }
      ]
    };
  },

  props: ["masterCompanyMap", "AWS", "cognitoidentityserviceprovider"],

  computed: {
    ...mapState(["UserName", "PoolData"]),

    // function to display the list of companies in the master company list.
    displayCompanies() {
      let companyList = [];
      this.masterCompanyMap.forEach((companyObject, companyName) => {
        companyList.push(companyName);
      });
      return companyList.sort();
    },

    handleDepotData() {
      // copy data over, assign key to each record, and sort/filter it according to requirements.
      const data = this.depotData;
      var totalChargerCount = 0;
      data
        .sort((recordOne, recordTwo) => {
          return recordOne.entityId.localeCompare(recordTwo.entityId);
        })
        .forEach((depotObject, outerIndex) => {
          depotObject.key = outerIndex + 1;
          depotObject.chargerList = JSON.parse(depotObject.chargerList);
          depotObject.numberOfChargers = depotObject.chargerList.length;
          totalChargerCount += depotObject.chargerList.length;
          depotObject.chargerList.forEach((charger, innerIndex) => {
            charger.key = innerIndex + 1;
            charger.revision = this.$moment(charger.revision)
              .local()
              .format("YYYY-MM-DD HH:mm");
          });
        });
      this.setTotalNumberOfChargers(totalChargerCount);
      return data;
    },

    // function to display list of available users in a tree structure.
    userTreeData() {
      var authorizedUserList = [];
      var listUsersParams = {
        UserPoolId: this.PoolData.UserPoolId,
        AttributesToGet: ["custom:company", "custom:authorizedCompanies", "name"]
      };
      this.cognitoidentityserviceprovider.listUsers(listUsersParams, (error, data) => {
        if (error) {
          this.$message.error("Error retrieving users from User Pool.");
          return [];
        } else {
          if (this.masterCompanyMap) {
            const userData = data.Users;
            this.saveUserData(userData);
            let companyList = [];
            this.masterCompanyMap.forEach((companyObject, companyName) => {
              companyList.push(companyName);
            });
            // also add I/O Controls and BYD.
            companyList.push("I/O Controls");
            companyList.push("BYD");
            companyList.sort();
            companyList.forEach(companyName => {
              let companyObject = {};
              companyObject.title = companyName;
              companyObject.value = companyName;
              companyObject.key = companyName;
              companyObject.children = [];
              let userList = userData.filter(user => {
                return user.Attributes[2].Value === companyName;
              });
              if (userList.length) {
                userList.forEach(userObject => {
                  companyObject.children.push({
                    title: userObject.Attributes[0].Value,
                    key: userObject.Username,
                    value: userObject.Username
                  });
                });
                authorizedUserList.push(companyObject);
              }
            });
          }
        }
      });
      return authorizedUserList;
    },

    moveChargersButtonDisabled() {
      return !(this.selectedRowKeys.length == 2 && this.selectedRows[0].company == this.selectedRows[1].company);
    },

    hasSelectedMultiple() {
      let otherDepots = this.selectedRows.filter(depot => {
        return depot.company !== this.selectedRows[0].company;
      });
      return this.selectedRowKeys.length >= 2 && !otherDepots.length;
    }
  },

  methods: {
    handleDepotSearch(e) {
      e.preventDefault();
      this.depotSearchForm.validateFields((error, fieldValues) => {
        if (error) {
          return;
        }
        this.loadDepotData(fieldValues.company);
      });
    },

    editCharger(event) {
      event.preventDefault();
      this.editChargerForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          this.editChargerFormButtonLoading = true;
          let newDepotRecord = cloneDeep(this.editChargerFormInitialValues);
          let currentChargerList = newDepotRecord.chargerList.filter(charger => {
            return charger.key !== newDepotRecord.selectedChargerKey;
          });
          let newChargerRecord = {
            chargerNumber: fieldValues.chargerNumber,
            macID: fieldValues.macID.trim(),
            priority: fieldValues.priority,
            revision: new Date(),
            entityId: "CHG#" + fieldValues.macID.trim(),
            depot: fieldValues.depotName
          };
          currentChargerList.push(newChargerRecord);
          newDepotRecord.chargerList = currentChargerList;
          // remove all the unnecessary initial values before inserting the updated depotRecord into the database.
          delete newDepotRecord.selectedChargerKey;
          delete newDepotRecord.selectedMacID;
          delete newDepotRecord.selectedChargerNumber;
          delete newDepotRecord.selectedPriority;
          var params = {
            objectType: "DPO",
            operation: "update",
            region: this.masterCompanyMap.get(newDepotRecord.company).region
          };
          var body = {
            depotRecord: this.prepareForDatabase(newDepotRecord),
            insertChargerObjects: true
          };
          await this.sendRequest(params, body, "post");
          let isServerUpdated = await this.updateSingleServerProgram(
            newDepotRecord.depot,
            this.masterCompanyMap.get(newDepotRecord.company).region,
            "chargerList"
          );
          if (!(isServerUpdated === true)) {
            throw new Error("Unable to update server program. Please try again or re-sync the server!");
          }
          this.editChargerFormButtonLoading = false;
          this.editChargerForm.resetFields();
          this.editChargerModalVisible = false;
          this.$message.success("Successfully updated charger and server program!");
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        } catch (error) {
          this.editChargerFormButtonLoading = false;
          this.editChargerForm.resetFields();
          this.editChargerModalVisible = false;
          this.$message.error("Error updating charger - " + (error.message ? error.message : error));
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
          return;
        }
      });
    },

    createDepot(event) {
      event.preventDefault();
      this.createDepotForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          this.createDepotFormButtonLoading = true;
          this.createDepotLoadingPercentage = 25;
          let chargerList = JSON.parse(fieldValues.chargerList.replace(/\s+/g, ""));
          chargerList.forEach(chargerRecord => {
            chargerRecord.depot = fieldValues.depot;
            chargerRecord.entityId = "CHG#" + chargerRecord.macID;
            chargerRecord.revision = new Date();
          });
          let userList = fieldValues.userList.replace(/\s+/g, "");
          let timeSlots = fieldValues.timeSlots.replace(/\s+/g, "");
          let newDepotRecord = {
            certificateFile: fieldValues.certificateFile.trim(),
            chargerList: JSON.stringify(chargerList),
            clientEndpoint: fieldValues.clientEndpoint.trim(),
            company: fieldValues.company,
            depot: fieldValues.depot.trim(),
            entityId: "DPO#" + fieldValues.depot.trim(),
            isActive: true,
            maxChargingCurrent: fieldValues.maxChargingCurrent,
            maxConcurrentChargers: fieldValues.maxConcurrentChargers,
            maxSocToCharge: fieldValues.maxSocToCharge,
            minimumSocRequiredForPauseCommand: fieldValues.minimumSocRequiredForPauseCommand,
            overriddenChargerCount: fieldValues.overriddenChargerCount === "true",
            sendSNSAlert: fieldValues.sendSNSAlert === "true",
            privateKeyFile: fieldValues.privateKeyFile.trim(),
            revision: new Date(),
            SOCThresholdHigh: fieldValues.SOCThresholdHigh,
            SOCThresholdMedian: fieldValues.SOCThresholdMedian,
            timeoutForChargingDisconnect: fieldValues.timeoutForChargingDisconnect,
            timeoutForReadyDisconnect: fieldValues.timeoutForReadyDisconnect,
            timeoutForDisconnectAlert: fieldValues.timeoutForDisconnectAlert,
            timeSlots: timeSlots,
            userList: userList,
            fleetList: JSON.stringify(fieldValues.fleetList),
            utcHoursOffset: !fieldValues.utcHoursOffset ? 0 : fieldValues.utcHoursOffset,
            programVersion: "V" + fieldValues.programVersion.trim(),
            updatedBy: this.UserName
          };
          this.createDepotLoadingPercentage = 50;
          var params = {
            objectType: "DPO",
            operation: "add",
            entityId: "DPO#" + fieldValues.depot.trim(),
            company: fieldValues.company,
            revision: new Date().toISOString(),
            username: this.UserName,
            region: this.masterCompanyMap.get(fieldValues.company).region
          };
          var body = {
            depotRecord: JSON.stringify(newDepotRecord)
          };
          await this.sendRequest(params, body, "post");
          this.createDepotLoadingPercentage = 75;
          // finally, iterate through the list of users given access to this new depot and update
          // their authorized companies list.
          if (fieldValues.authorizedUsers) {
            var promiseArray = [];
            fieldValues.authorizedUsers.forEach(username => {
              let currentUserRecord = this.userData.find(userRecord => userRecord.Username === username);
              let authorizedCompanies = JSON.parse(currentUserRecord.Attributes[1].Value);
              let companyName = fieldValues.company;
              let companyFound = false;
              authorizedCompanies.forEach(companyObject => {
                if (companyObject.company === companyName) {
                  companyObject.depot.push(fieldValues.depot.trim());
                  companyFound = true;
                }
              });
              // if the company does not exist in the user's authoriedCompanies list, create a new companyObject.
              if (!companyFound) {
                authorizedCompanies.push({
                  company: companyName,
                  depot: [fieldValues.depot.trim()],
                  region: this.masterCompanyMap.get(companyName).region,
                  hasTransformer: this.masterCompanyMap.get(companyName).hasTransformer
                });
              }
              let updateParams = {
                UserPoolId: this.PoolData.UserPoolId,
                Username: username,
                UserAttributes: [
                  {
                    Name: "custom:authorizedCompanies",
                    Value: JSON.stringify(authorizedCompanies)
                  }
                ]
              };
              // create a promise to update the authorizedCompanies user attribute.
              var updateUserAttributesPromise = this.cognitoidentityserviceprovider
                .adminUpdateUserAttributes(updateParams)
                .promise();
              promiseArray.push(updateUserAttributesPromise);
            });
            // wait for all the promises to return.
            await Promise.all(promiseArray);
          }
          this.createDepotLoadingPercentage = 100;
          this.createDepotFormButtonLoading = false;
          this.createDepotForm.resetFields();
          this.createDepotModalVisible = false;
          this.$message.success("Successfully created new depot. Please refresh data if you do not see the new depot.");
          this.$emit("refresh-company-list");
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        } catch (error) {
          this.createDepotFormButtonLoading = false;
          if (error == "The conditional request failed") {
            this.$message.error("Error creating new depot - Depot Name already exists.");
          } else {
            this.$message.error("Error creating new depot - " + (error.message ? error.message : error));
            this.createDepotForm.resetFields();
            this.createDepotModalVisible = false;
            this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
          }
          return;
        }
      });
    },

    sendRequest(params, body, requestType) {
      // before making an API request, check to make sure user has access to perform the action / credentials need to be refreshed.
      return new Promise((resolve, reject) => {
        this.AWS.config.credentials.get(async credentialError => {
          if (credentialError) {
            if (credentialError.name === "NotAuthorizedException") {
              reject(
                new Error(
                  "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                )
              );
            } else {
              reject(credentialError.message);
            }
          } else {
            try {
              var aws4 = require("aws4");
              let paramsUrl = this.createParamsUrl(params);
              switch (requestType) {
                case "get": {
                  let request = {
                    host: "v9ovun9ana.execute-api.us-west-2.amazonaws.com",
                    method: "GET",
                    url: "https://v9ovun9ana.execute-api.us-west-2.amazonaws.com/prod/configure?" + paramsUrl,
                    path: "/prod/configure?" + paramsUrl,
                    service: "execute-api"
                  };
                  let signedRequest = aws4.sign(request, {
                    secretAccessKey: this.AWS.config.credentials.secretAccessKey,
                    accessKeyId: this.AWS.config.credentials.accessKeyId,
                    sessionToken: this.AWS.config.credentials.sessionToken
                  });
                  // delete unsafe headers from the signed request.
                  delete signedRequest.headers["Host"];
                  delete signedRequest.headers["Content-Length"];

                  let response = await this.$axios(signedRequest);
                  let items = response.data.Items;
                  resolve(items);
                  break;
                }
                case "post": {
                  let request = {
                    host: "v9ovun9ana.execute-api.us-west-2.amazonaws.com",
                    method: "POST",
                    url: "https://v9ovun9ana.execute-api.us-west-2.amazonaws.com/prod/configure?" + paramsUrl,
                    path: "/prod/configure?" + paramsUrl,
                    service: "execute-api",
                    data: body,
                    body: JSON.stringify(body),
                    headers: {
                      "content-type": "application/json"
                    }
                  };
                  let signedRequest = aws4.sign(request, {
                    secretAccessKey: this.AWS.config.credentials.secretAccessKey,
                    accessKeyId: this.AWS.config.credentials.accessKeyId,
                    sessionToken: this.AWS.config.credentials.sessionToken
                  });
                  // delete unsafe headers from the signed request.
                  delete signedRequest.headers["Host"];
                  delete signedRequest.headers["Content-Length"];

                  let response = await this.$axios(signedRequest);
                  let responseData = response.data;
                  resolve(responseData);
                  break;
                }
                default:
                  break;
              }
            } catch (error) {
              // if no response is available, means it is a network error.
              if (!error.response) {
                reject(new Error("Network Error. Please try again!"));
              } else {
                let response = error.response;
                if (response.status == 403) {
                  reject(
                    "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                  );
                } else {
                  reject(response.data.message);
                }
              }
            }
          }
        });
      });
    },

    updateSingleServerProgram(depotName, region, updateAttribute) {
      return new Promise((resolve, reject) => {
        var AWSIoTData = require("aws-iot-device-sdk");
        var isUpdated = false;
        this.AWS.config.credentials.get(identityError => {
          if (!identityError) {
            try {
              const mqttClient = AWSIoTData.device({
                region: region,
                host: "a3ijg5gjke8m6j-ats.iot." + region + ".amazonaws.com",
                clientId: "elmsWebsiteClient_" + this.UserName,
                protocol: "wss",
                debug: false,
                reconnectPeriod: 0,
                accessKeyId: this.AWS.config.credentials.accessKeyId,
                secretKey: this.AWS.config.credentials.secretAccessKey,
                sessionToken: this.AWS.config.credentials.sessionToken
              });
              mqttClient.on("close", () => {
                // end the client connection again in case connection was automatically closed.
                mqttClient.end();
                resolve(isUpdated);
              });
              mqttClient.on("connect", () => {
                // on connection, firstly subscribe to update confirmation topic.
                mqttClient.subscribe("ioc/" + depotName + "/elmsServer/cmd/confirmServerUpdate", { qos: 1 });
                // next, publish message to update the requested attribute and set a timeout period to end the client connection.
                let messagePayload = {
                  updateAttribute: updateAttribute,
                  timestamp: new Date()
                };
                mqttClient.publish(
                  "ioc/" + depotName + "/elmsServer/cmd/issueServerUpdate",
                  JSON.stringify(messagePayload),
                  { qos: 1 }
                );
                setTimeout(() => {
                  // once timeout is reached, end the client connection.
                  mqttClient.end();
                }, 2500);
              });
              mqttClient.on("error", () => {
                throw new Error("Error thrown by MQTT Client.");
              });
              mqttClient.on("message", (topic, payload) => {
                isUpdated = JSON.parse(payload).isUpdated;
                mqttClient.end();
              });
            } catch (error) {
              reject(error);
            }
          } else {
            if (identityError.name === "NotAuthorizedException") {
              reject(
                new Error(
                  "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                )
              );
            } else {
              reject(new Error(identityError.message));
            }
          }
        });
      });
    },

    updateMultipleServerPrograms(depotNames, region, updateAttribute) {
      return new Promise((resolve, reject) => {
        var AWSIoTData = require("aws-iot-device-sdk");
        var counter = 0,
          totalCount = depotNames.length;
        this.AWS.config.credentials.get(identityError => {
          if (!identityError) {
            try {
              const mqttClient = AWSIoTData.device({
                region: region,
                host: "a3ijg5gjke8m6j-ats.iot." + region + ".amazonaws.com",
                clientId: "elmsWebsiteClient_" + this.UserName,
                protocol: "wss",
                debug: false,
                reconnectPeriod: 0,
                accessKeyId: this.AWS.config.credentials.accessKeyId,
                secretKey: this.AWS.config.credentials.secretAccessKey,
                sessionToken: this.AWS.config.credentials.sessionToken
              });
              mqttClient.on("close", () => {
                // end the client connection again in case connection was automatically closed.
                mqttClient.end();
                resolve(depotNames);
              });
              mqttClient.on("connect", () => {
                // on connection, firstly subscribe to update confirmation topic.
                mqttClient.subscribe("ioc/+/elmsServer/cmd/confirmServerUpdate", { qos: 1 });
                // next, publish message to update the requested attribute and set a timeout period to end the client connection.
                depotNames.forEach(depotName => {
                  let messagePayload = {
                    updateAttribute: updateAttribute,
                    timestamp: new Date()
                  };
                  mqttClient.publish(
                    "ioc/" + depotName + "/elmsServer/cmd/issueServerUpdate",
                    JSON.stringify(messagePayload),
                    { qos: 1 }
                  );
                });
                setTimeout(() => {
                  // once timeout is reached, end the client connection.
                  mqttClient.end();
                }, this.getConnectionTimeout(depotNames.length));
              });
              mqttClient.on("error", () => {
                throw new Error("Error thrown by MQTT Client.");
              });
              mqttClient.on("message", (topic, payload) => {
                var depotName = topic.split("/")[1];
                let isUpdated = JSON.parse(payload).isUpdated;
                if (isUpdated) {
                  depotNames = depotNames.filter(depot => depot !== depotName);
                }
                counter += 1;
                // this means we have received a response from all the depots and can close the connection.
                if (counter === totalCount) {
                  mqttClient.end();
                }
              });
            } catch (error) {
              reject(error);
            }
          } else {
            if (identityError.name === "NotAuthorizedException") {
              reject(
                new Error(
                  "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
                )
              );
            } else {
              reject(new Error(identityError.message));
            }
          }
        });
      });
    },

    async loadDepotData(selectedCompany) {
      this.depotDataLoading = true;
      this.depotData = [];
      this.selectedRowKeys = [];
      this.selectedRows = [];
      var promiseArray = [];
      // if the user has selected a company, iterate through the company's list of depots to create the query parameters.
      if (selectedCompany) {
        let listOfLocations = this.masterCompanyMap.get(selectedCompany).depots;
        // only proceed if the company has a non-empty list of depots.
        if (listOfLocations) {
          listOfLocations.forEach(depot => {
            let params = {
              entityId: "DPO#" + depot,
              objectType: "DPO",
              operation: "get",
              region: this.masterCompanyMap.get(selectedCompany).region
            };
            promiseArray.push(this.sendRequest(params, {}, "get"));
          });
        }
      } else {
        // else iterate through every company's list of depots to create the query parameters.
        this.masterCompanyMap.forEach(valueObject => {
          let listOfLocations = valueObject.depots;
          // only proceed if the company has a non-empty list of depots.
          if (listOfLocations) {
            listOfLocations.forEach(depot => {
              let params = {
                entityId: "DPO#" + depot,
                objectType: "DPO",
                operation: "get",
                region: valueObject.region
              };
              promiseArray.push(this.sendRequest(params, {}, "get"));
            });
          }
        });
      }
      try {
        let dataArray = await Promise.all(promiseArray);
        let data = dataArray.flat(Infinity);
        // check if no data is returned; display a message to user in that case.
        if (!data.length) {
          throw new Error("No data available for selected company!");
        }
        await this.getMultipleSyncStatus(data);
        this.depotData = data;
        this.depotEntriesFound = data.length;
      } catch (error) {
        this.$message.error("Error loading depot data: " + (error.message ? error.message : error));
        this.depotDataLoading = false;
        return;
      }
      this.depotDataLoading = false;
    },

    getMultipleSyncStatus(depotData) {
      return new Promise(resolve => {
        var AWSIoTData = require("aws-iot-device-sdk");
        this.AWS.config.credentials.get(identityError => {
          if (!identityError) {
            try {
              var depotData_usa = depotData.filter(
                depotObject => this.masterCompanyMap.get(depotObject.company).region == "us-west-2"
              );
              var depotData_sa = depotData.filter(
                depotObject => this.masterCompanyMap.get(depotObject.company).region == "sa-east-1"
              );
              var counter_usa = 0,
                counter_sa = 0;
              var mqttClient_usa, mqttClient_sa;
              if (depotData_sa.length) {
                mqttClient_sa = AWSIoTData.device({
                  region: "sa-east-1",
                  host: "a3ijg5gjke8m6j-ats.iot.sa-east-1.amazonaws.com",
                  clientId: "elmsWebsiteClient_sa-east-1_" + this.UserName,
                  protocol: "wss",
                  debug: false,
                  reconnectPeriod: 0,
                  accessKeyId: this.AWS.config.credentials.accessKeyId,
                  secretKey: this.AWS.config.credentials.secretAccessKey,
                  sessionToken: this.AWS.config.credentials.sessionToken
                });
                mqttClient_sa.on("close", () => {
                  // end the client connection again in case connection was automatically closed.
                  mqttClient_sa.end();
                });
                mqttClient_sa.on("connect", () => {
                  // on connection, firstly subscribe to update confirmation topics for all depots.
                  mqttClient_sa.subscribe("ioc/+/elmsServer/cmd/confirmServerUpdate", {
                    qos: 1
                  });
                  depotData_sa.forEach(depotObject => {
                    depotObject.syncStatus = "error";
                    // next, publish message to request the depotObjectRevision for each depot.
                    let messagePayload = {
                      updateAttribute: "depotObjectRevision",
                      timestamp: new Date()
                    };
                    mqttClient_sa.publish(
                      "ioc/" + depotObject.depot + "/elmsServer/cmd/issueServerUpdate",
                      JSON.stringify(messagePayload),
                      { qos: 1 }
                    );
                  });
                });
                mqttClient_sa.on("error", () => {
                  throw new Error("Error thrown by MQTT Client.");
                });
                mqttClient_sa.on("message", (topic, payload) => {
                  var depotName = topic.split("/")[1];
                  let reportedRevision = JSON.parse(payload).revision;
                  let depotObject = depotData_sa.find(depotObject => depotObject.depot === depotName);
                  if (depotObject) {
                    if (reportedRevision === depotObject.revision) {
                      depotObject.syncStatus = "true";
                    } else {
                      depotObject.syncStatus = "false";
                    }
                    counter_sa += 1;
                    // this means we have received a response from all the depots and can close the connection.
                    if (counter_sa === depotData_sa.length) {
                      mqttClient_sa.end();
                      // only resolve and return from the method if no depot data for USA is present.
                      if (!depotData_usa.length) {
                        resolve();
                      }
                    }
                  }
                });
              }
              if (depotData_usa.length) {
                mqttClient_usa = AWSIoTData.device({
                  region: "us-west-2",
                  host: "a3ijg5gjke8m6j-ats.iot.us-west-2.amazonaws.com",
                  clientId: "elmsWebsiteClient_us-west-2_" + this.UserName,
                  protocol: "wss",
                  debug: false,
                  reconnectPeriod: 0,
                  accessKeyId: this.AWS.config.credentials.accessKeyId,
                  secretKey: this.AWS.config.credentials.secretAccessKey,
                  sessionToken: this.AWS.config.credentials.sessionToken
                });
                mqttClient_usa.on("close", () => {
                  // end the client connection again in case connection was automatically closed.
                  mqttClient_usa.end();
                });
                mqttClient_usa.on("connect", () => {
                  // on connection, firstly subscribe to update confirmation topics for all depots.
                  mqttClient_usa.subscribe("ioc/+/elmsServer/cmd/confirmServerUpdate", {
                    qos: 1
                  });
                  depotData_usa.forEach(depotObject => {
                    depotObject.syncStatus = "error";
                    // next, publish message to request the depotObjectRevision for each depot.
                    let messagePayload = {
                      updateAttribute: "depotObjectRevision",
                      timestamp: new Date()
                    };
                    mqttClient_usa.publish(
                      "ioc/" + depotObject.depot + "/elmsServer/cmd/issueServerUpdate",
                      JSON.stringify(messagePayload),
                      { qos: 1 }
                    );
                  });
                });
                mqttClient_usa.on("error", () => {
                  throw new Error("Error thrown by MQTT Client.");
                });
                mqttClient_usa.on("message", (topic, payload) => {
                  var depotName = topic.split("/")[1];
                  let reportedRevision = JSON.parse(payload).revision;
                  let depotObject = depotData_usa.find(depotObject => depotObject.depot === depotName);
                  if (depotObject) {
                    if (reportedRevision === depotObject.revision) {
                      depotObject.syncStatus = "true";
                    } else {
                      depotObject.syncStatus = "false";
                    }
                    counter_usa += 1;
                    // this means we have received a response from all the depots and can close the connection.
                    if (counter_usa === depotData_usa.length) {
                      mqttClient_usa.end();
                      // only resolve and return from the method if no depot data for SA is present.
                      if (!depotData_sa.length) {
                        resolve();
                      }
                    }
                  }
                });
              }
              setTimeout(() => {
                // once timeout is reached, end the client connections.
                if (mqttClient_usa) {
                  mqttClient_usa.end();
                }
                if (mqttClient_sa) {
                  mqttClient_sa.end();
                }
                resolve();
              }, this.getConnectionTimeout(depotData_sa.length + depotData_usa.length));
            } catch (error) {
              resolve();
            }
          } else {
            resolve();
          }
        });
      });
    },

    getSingleSyncStatus(depotObject) {
      var AWSIoTData = require("aws-iot-device-sdk");
      var region = this.masterCompanyMap.get(depotObject.company).region;
      this.AWS.config.credentials.get(identityError => {
        if (!identityError) {
          try {
            this.depotDataLoading = true;
            depotObject.syncStatus = "error";
            const mqttClient = AWSIoTData.device({
              region: region,
              host: "a3ijg5gjke8m6j-ats.iot." + region + ".amazonaws.com",
              clientId: "elmsWebsiteClient_" + this.UserName,
              protocol: "wss",
              debug: false,
              reconnectPeriod: 0,
              accessKeyId: this.AWS.config.credentials.accessKeyId,
              secretKey: this.AWS.config.credentials.secretAccessKey,
              sessionToken: this.AWS.config.credentials.sessionToken
            });
            mqttClient.on("close", () => {
              // end the client connection again in case connection was automatically closed.
              mqttClient.end();
              this.depotDataLoading = false;
            });
            mqttClient.on("connect", () => {
              // on connection, firstly subscribe to update confirmation topic.
              mqttClient.subscribe("ioc/" + depotObject.depot + "/elmsServer/cmd/confirmServerUpdate", { qos: 1 });
              // next, publish message to update the requested attribute and set a timeout period to end the client connection.
              let messagePayload = {
                updateAttribute: "depotObjectRevision",
                timestamp: new Date()
              };
              mqttClient.publish(
                "ioc/" + depotObject.depot + "/elmsServer/cmd/issueServerUpdate",
                JSON.stringify(messagePayload),
                { qos: 1 }
              );
              setTimeout(() => {
                // once timeout is reached, end the client connection.
                mqttClient.end();
              }, 2500);
            });
            mqttClient.on("error", () => {
              throw new Error("Error thrown by MQTT Client.");
            });
            mqttClient.on("message", (topic, payload) => {
              let reportedRevision = JSON.parse(payload).revision;
              if (reportedRevision === depotObject.revision) {
                depotObject.syncStatus = "true";
              } else {
                depotObject.syncStatus = "false";
              }
              mqttClient.end();
            });
          } catch (error) {
            this.depotDataLoading = false;
          }
        } else {
          if (identityError.name === "NotAuthorizedException") {
            this.$message.error(
              "You are not authorized to perform this action. Please contact Support Team if you think this is a mistake."
            );
          } else {
            this.$message.error("Error retrieving depot's sync status - " + identityError.message);
          }
        }
      });
    },

    addChargers(formEvent) {
      formEvent.preventDefault();
      this.addChargerForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          this.addChargerFormButtonLoading = true;
          let newDepotRecord = cloneDeep(this.addChargerFormInitialValues);
          let currentChargerList = newDepotRecord.chargerList;
          let addedChargerList = JSON.parse(fieldValues.chargerList.replace(/\s+/g, ""));
          addedChargerList.forEach(chargerRecord => {
            chargerRecord.depot = newDepotRecord.depot;
            chargerRecord.entityId = "CHG#" + chargerRecord.macID;
            chargerRecord.revision = new Date();
          });
          currentChargerList.push(...addedChargerList);
          newDepotRecord.chargerList = currentChargerList;
          var params = {
            objectType: "DPO",
            operation: "update",
            region: this.masterCompanyMap.get(newDepotRecord.company).region
          };
          var body = {
            depotRecord: this.prepareForDatabase(newDepotRecord),
            insertChargerObjects: true
          };
          await this.sendRequest(params, body, "post");
          let isServerUpdated = await this.updateSingleServerProgram(
            newDepotRecord.depot,
            this.masterCompanyMap.get(newDepotRecord.company).region,
            "chargerList"
          );
          if (!(isServerUpdated === true)) {
            throw new Error("Unable to update server program. Please try again or re-sync the server!");
          }
          this.addChargerFormButtonLoading = false;
          this.addChargerForm.resetFields();
          this.addChargerModalVisible = false;
          this.$message.success("Successfully added chargers and updated server program!");
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        } catch (error) {
          this.addChargerFormButtonLoading = false;
          this.addChargerForm.resetFields();
          this.addChargerModalVisible = false;
          this.$message.error("Error adding chargers - " + (error.message ? error.message : error));
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
          return;
        }
      });
    },

    saveConfiguration(formEvent) {
      formEvent.preventDefault();
      this.editConfigurationForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          this.editConfigurationFormButtonLoading = true;
          // update the values from the form, remove the keys, and make an API request.
          let newDepotRecord = cloneDeep(this.editConfigurationFormInitialValues);
          newDepotRecord.fleetList = JSON.stringify(fieldValues.fleetList);
          newDepotRecord.overriddenChargerCount = fieldValues.overriddenChargerCount == "true";
          newDepotRecord.sendSNSAlert = fieldValues.sendSNSAlert === "true";
          newDepotRecord.utcHoursOffset = fieldValues.utcHoursOffset;
          newDepotRecord.maxChargingCurrent = fieldValues.maxChargingCurrent;
          newDepotRecord.maxConcurrentChargers = fieldValues.maxConcurrentChargers;
          newDepotRecord.maxSocToCharge = fieldValues.maxSocToCharge;
          newDepotRecord.minimumSocRequiredForPauseCommand = fieldValues.minimumSocRequiredForPauseCommand;
          newDepotRecord.timeoutForReadyDisconnect = fieldValues.timeoutForReadyDisconnect;
          newDepotRecord.timeoutForChargingDisconnect = fieldValues.timeoutForChargingDisconnect;
          newDepotRecord.timeoutForDisconnectAlert = fieldValues.timeoutForDisconnectAlert;
          newDepotRecord.certificateFile = fieldValues.certificateFile.trim();
          newDepotRecord.clientEndpoint = fieldValues.clientEndpoint.trim();
          newDepotRecord.privateKeyFile = fieldValues.privateKeyFile.trim();
          newDepotRecord.SOCThresholdHigh = fieldValues.SOCThresholdHigh;
          newDepotRecord.SOCThresholdMedian = fieldValues.SOCThresholdMedian;
          var params = {
            objectType: "DPO",
            operation: "update",
            region: this.masterCompanyMap.get(newDepotRecord.company).region
          };
          var body = {
            depotRecord: this.prepareForDatabase(newDepotRecord)
          };
          await this.sendRequest(params, body, "post");
          let isServerUpdated = await this.updateSingleServerProgram(
            newDepotRecord.depot,
            this.masterCompanyMap.get(newDepotRecord.company).region,
            "configurationProperties"
          );
          if (!(isServerUpdated === true)) {
            throw new Error("Unable to update server program. Please try again or re-sync the server!");
          }
          this.editConfigurationFormButtonLoading = false;
          this.editConfigurationForm.resetFields();
          this.editConfigurationModalVisible = false;
          this.$message.success("Successfully saved configuration settings and updated server program!");
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        } catch (error) {
          this.editConfigurationFormButtonLoading = false;
          this.editConfigurationForm.resetFields();
          this.editConfigurationModalVisible = false;
          this.$message.error("Error saving configuration settings - " + (error.message ? error.message : error));
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
          return;
        }
      });
    },

    async saveChargingSchedule(formEvent) {
      formEvent.preventDefault();
      if (!this.chargingScheduleDataSource.length) {
        this.$message.error("Enter at least one time slot!");
        return;
      }
      try {
        // update the values from the form, remove the keys, and make an API request.
        this.chargingScheduleButtonLoading = true;
        let newDepotRecord = cloneDeep(this.chargingScheduleIntitialValues);
        let newTimeSlots = cloneDeep(this.chargingScheduleDataSource);
        newTimeSlots.forEach(timeSlot => delete timeSlot.rowKey);
        newDepotRecord.timeSlots = JSON.stringify(newTimeSlots);
        var params = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(newDepotRecord.company).region
        };
        var body = {
          depotRecord: this.prepareForDatabase(newDepotRecord)
        };
        await this.sendRequest(params, body, "post");
        let isServerUpdated = await this.updateSingleServerProgram(
          newDepotRecord.depot,
          this.masterCompanyMap.get(newDepotRecord.company).region,
          "timeSlots"
        );
        if (!(isServerUpdated === true)) {
          throw new Error("Unable to update server program. Please try again or re-sync the server!");
        }
        this.chargingScheduleForm.resetFields();
        this.chargingScheduleButtonLoading = false;
        this.chargingScheduleModalVisible = false;
        this.$message.success("Successfully saved charging restriction schedule and updated server program!");
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
      } catch (error) {
        this.chargingScheduleForm.resetFields();
        this.chargingScheduleButtonLoading = false;
        this.chargingScheduleModalVisible = false;
        this.$message.error("Error saving charging restriction schedule - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        return;
      }
    },

    async saveUserList(formEvent) {
      formEvent.preventDefault();
      if (!this.userListDataSource.length) {
        this.$message.error("Enter at least one time authorized user!");
        return;
      }
      try {
        // update the values from the form, remove the keys, and make an API request.
        this.userListButtonLoading = true;
        let newDepotRecord = cloneDeep(this.userListInitialValues);
        let newUserList = cloneDeep(this.userListDataSource);
        newUserList.forEach(user => delete user.rowKey);
        newDepotRecord.userList = JSON.stringify(newUserList);
        var params = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(newDepotRecord.company).region
        };
        var body = {
          depotRecord: this.prepareForDatabase(newDepotRecord)
        };
        await this.sendRequest(params, body, "post");
        let isServerUpdated = await this.updateSingleServerProgram(
          newDepotRecord.depot,
          this.masterCompanyMap.get(newDepotRecord.company).region,
          "userList"
        );
        if (!(isServerUpdated === true)) {
          throw new Error("Unable to update server program. Please try again or re-sync the server!");
        }
        this.userListForm.resetFields();
        this.userListButtonLoading = false;
        this.userListModalVisible = false;
        this.$message.success("Successfully saved user list and updated server program!");
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
      } catch (error) {
        this.userListForm.resetFields();
        this.userListButtonLoading = false;
        this.userListModalVisible = false;
        this.$message.error("Error saving user list - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        return;
      }
    },

    async deleteCharger(currentDepotRecord, chargerRecord) {
      try {
        this.depotDataLoading = true;
        let newDepotRecord = cloneDeep(currentDepotRecord);
        let newChargerList = newDepotRecord.chargerList.filter(charger => {
          return charger.key !== chargerRecord.key;
        });
        newDepotRecord.chargerList = newChargerList;
        var params = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(newDepotRecord.company).region
        };
        var body = {
          depotRecord: this.prepareForDatabase(newDepotRecord),
          insertChargerObjects: true
        };
        await this.sendRequest(params, body, "post");
        let isServerUpdated = await this.updateSingleServerProgram(
          newDepotRecord.depot,
          this.masterCompanyMap.get(newDepotRecord.company).region,
          "chargerList"
        );
        if (!(isServerUpdated === true)) {
          throw new Error("Unable to update server program. Please try again or re-sync the server!");
        }
        this.$message.success("Successfully deleted charger and updated server program!");
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
      } catch (error) {
        this.$message.error("Error deleting charger - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        return;
      }
    },

    addUser(formEvent) {
      formEvent.preventDefault();
      this.addUserForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          // depending on from where this method was called, add the time slot to the appropriate place.
          if (this.createDepotModalVisible) {
            let currentUserList = this.createDepotForm.getFieldValue("userList");
            // if a userList already exists, add to the list.
            if (currentUserList) {
              let jsonArray = JSON.parse(currentUserList);
              jsonArray.push({ username: fieldValues.username.trim(), pin: fieldValues.pin.trim() });
              this.createDepotForm.setFieldsValue({ userList: JSON.stringify(jsonArray) });
            } else {
              let jsonArray = [];
              jsonArray.push({ username: fieldValues.username.trim(), pin: fieldValues.pin.trim() });
              this.createDepotForm.setFieldsValue({ userList: JSON.stringify(jsonArray) });
            }
            this.addUserModalVisible = false;
          } else {
            let currentDataSource = [...this.userListDataSource];
            currentDataSource.push({
              username: fieldValues.username.trim(),
              pin: fieldValues.pin.trim()
            });
            this.userListDataSource = currentDataSource;
            this.addUserModalVisible = false;
          }
        } catch (error) {
          this.$message.error("Error adding user - " + (error.message ? error.message : error));
          this.addUserModalVisible = false;
          return;
        }
      });
    },

    deleteTimeSlot(timeSlotKey) {
      const currentDataSource = [...this.chargingScheduleDataSource];
      this.chargingScheduleDataSource = currentDataSource.filter(timeSlot => timeSlot.rowKey !== timeSlotKey);
      this.allowUnrestrictedCharging = this.checkZeroTimeSlot(this.chargingScheduleDataSource);
    },

    deleteUser(userKey) {
      const currentDataSource = [...this.userListDataSource];
      this.userListDataSource = currentDataSource.filter(user => user.rowKey !== userKey);
    },

    addTimeSlot(formEvent) {
      formEvent.preventDefault();
      this.addTimeSlotForm.validateFields((error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          // depending on from where this method was called, add the time slot to the appropriate place.
          if (this.createDepotModalVisible) {
            let currentTimeSlots = this.createDepotForm.getFieldValue("timeSlots");
            // if a timeSlot list already exists, add to the list.
            if (currentTimeSlots) {
              let jsonArray = JSON.parse(currentTimeSlots);
              jsonArray.push({
                noChargingStartTime: fieldValues.noChargingStartTime.format("HH:mm").toString(),
                noChargingStopTime: fieldValues.noChargingStopTime.format("HH:mm").toString(),
                isWeekendExcluded: fieldValues.isWeekendExcluded === "true",
                isOvernight: fieldValues.isOvernight === "true"
              });
              this.createDepotForm.setFieldsValue({ timeSlots: JSON.stringify(jsonArray) });
            } else {
              let jsonArray = [];
              jsonArray.push({
                noChargingStartTime: fieldValues.noChargingStartTime.format("HH:mm").toString(),
                noChargingStopTime: fieldValues.noChargingStopTime.format("HH:mm").toString(),
                isWeekendExcluded: fieldValues.isWeekendExcluded === "true",
                isOvernight: fieldValues.isOvernight === "true"
              });
              this.createDepotForm.setFieldsValue({ timeSlots: JSON.stringify(jsonArray) });
            }
            this.addTimeSlotModalVisible = false;
          } else if (this.chargingScheduleModalVisible) {
            let currentDataSource = [...this.chargingScheduleDataSource];
            currentDataSource.push({
              noChargingStartTime: fieldValues.noChargingStartTime.format("HH:mm").toString(),
              noChargingStopTime: fieldValues.noChargingStopTime.format("HH:mm").toString(),
              isWeekendExcluded: fieldValues.isWeekendExcluded === "true",
              isOvernight: fieldValues.isOvernight === "true",
              rowKey: currentDataSource.length + 1
            });
            this.chargingScheduleDataSource = currentDataSource;
            this.allowUnrestrictedCharging = this.checkZeroTimeSlot(this.chargingScheduleDataSource);
            this.addTimeSlotModalVisible = false;
          } else {
            this.handleGroupAddTimeSlot(fieldValues);
          }
        } catch (error) {
          this.$message.error("Error adding time slot - " + (error.message ? error.message : error));
          this.addTimeSlotModalVisible = false;
          return;
        }
      });
    },

    editTimeSlot(formEvent) {
      formEvent.preventDefault();
      this.editTimeSlotForm.validateFields((error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          let currentDataSource = [...this.chargingScheduleDataSource];
          currentDataSource = currentDataSource.filter(
            timeSlot => timeSlot.rowKey !== this.editTimeSlotFormInitialValues.rowKey
          );
          currentDataSource.push({
            noChargingStartTime: fieldValues.noChargingStartTime.format("HH:mm").toString(),
            noChargingStopTime: fieldValues.noChargingStopTime.format("HH:mm").toString(),
            isWeekendExcluded: fieldValues.isWeekendExcluded === "true",
            isOvernight: fieldValues.isOvernight === "true"
          });
          this.chargingScheduleDataSource = currentDataSource;
          this.allowUnrestrictedCharging = this.checkZeroTimeSlot(this.chargingScheduleDataSource);
          this.editTimeSlotModalVisible = false;
        } catch (error) {
          this.$message.error("Error saving time slot - " + (error.message ? error.message : error));
          this.editTimeSlotModalVisible = false;
          return;
        }
      });
    },

    addIndividualCharger(formEvent) {
      formEvent.preventDefault();
      this.addIndividualChargerForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        try {
          let currentChargerList = this.addChargerForm.getFieldValue("chargerList");
          // if a chargerList already exists, add to the list.
          if (currentChargerList) {
            let jsonArray = JSON.parse(currentChargerList);
            jsonArray.push({
              chargerNumber: fieldValues.chargerNumber,
              macID: fieldValues.macID.trim(),
              priority: fieldValues.priority
            });
            this.addChargerForm.setFieldsValue({ chargerList: JSON.stringify(jsonArray) });
          } else {
            let jsonArray = [];
            jsonArray.push({
              chargerNumber: fieldValues.chargerNumber,
              macID: fieldValues.macID.trim(),
              priority: fieldValues.priority
            });
            this.addChargerForm.setFieldsValue({ chargerList: JSON.stringify(jsonArray) });
          }
          this.addIndividualChargerModalVisible = false;
        } catch (error) {
          this.$message.error("Error adding single charger - " + (error.message ? error.message : error));
          this.addIndividualChargerModalVisible = false;
          return;
        }
      });
    },

    async saveMoveChargers(formEvent) {
      formEvent.preventDefault();
      let error = this.moveChargersForm.getFieldError("transferTable");
      if (error) {
        return;
      }
      try {
        this.moveChargersLoadingPercentage = 20;
        this.moveChargersFormButtonLoading = true;
        let finalTargetKeys = this.moveChargersForm.getFieldValue("transferTable");
        let finalDataSource = cloneDeep(this.moveChargersData);
        let leftDepotRecord = cloneDeep(this.selectedRows[0]);
        leftDepotRecord.chargerList = finalDataSource.filter(
          chargerRecord => !finalTargetKeys.includes(chargerRecord.key)
        );
        leftDepotRecord.chargerList.forEach(chargerRecord => {
          // delete title if present and update the depot value.
          if (chargerRecord.title) {
            delete chargerRecord.title;
          }
          chargerRecord.depot = leftDepotRecord.depot;
        });
        let rightDepotRecord = cloneDeep(this.selectedRows[1]);
        rightDepotRecord.chargerList = finalDataSource.filter(chargerRecord =>
          finalTargetKeys.includes(chargerRecord.key)
        );
        rightDepotRecord.chargerList.forEach(chargerRecord => {
          // delete title if present and update the depot value.
          if (chargerRecord.title) {
            delete chargerRecord.title;
          }
          chargerRecord.depot = rightDepotRecord.depot;
        });
        let promiseArray = [];
        var leftDepotParams = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(leftDepotRecord.company).region
        };
        var leftDepotBody = {
          depotRecord: this.prepareForDatabase(leftDepotRecord),
          insertChargerObjects: true
        };
        promiseArray.push(this.sendRequest(leftDepotParams, leftDepotBody, "post"));
        var rightDepotParams = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(rightDepotRecord.company).region
        };
        var rightDepotBody = {
          depotRecord: this.prepareForDatabase(rightDepotRecord),
          insertChargerObjects: true
        };
        promiseArray.push(this.sendRequest(rightDepotParams, rightDepotBody, "post"));
        this.moveChargersLoadingPercentage = 40;
        await Promise.all(promiseArray);
        this.moveChargersLoadingPercentage = 60;
        let isLeftServerUpdated = await this.updateSingleServerProgram(
          leftDepotRecord.depot,
          this.masterCompanyMap.get(leftDepotRecord.company).region,
          "chargerList"
        );
        this.moveChargersLoadingPercentage = 80;
        let isRightServerUpdated = await this.updateSingleServerProgram(
          rightDepotRecord.depot,
          this.masterCompanyMap.get(rightDepotRecord.company).region,
          "chargerList"
        );
        if (!(isRightServerUpdated === true) || !(isLeftServerUpdated === true)) {
          throw new Error("Unable to update one of the server programs. Please try again or re-sync the servers!");
        }
        this.moveChargersLoadingPercentage = 100;
        this.moveChargersFormButtonLoading = false;
        this.moveChargersModalVisible = false;
        this.$message.success("Successfully updated charger lists and server programs!");
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
      } catch (error) {
        this.moveChargersFormButtonLoading = false;
        this.moveChargersModalVisible = false;
        this.$message.error("Error updating charger lists - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        return;
      }
    },

    async updateIsActive(depotRecord, isActive) {
      try {
        this.depotDataLoading = true;
        let newDepotRecord = cloneDeep(depotRecord);
        newDepotRecord.isActive = isActive;
        var params = {
          objectType: "DPO",
          operation: "update",
          region: this.masterCompanyMap.get(newDepotRecord.company).region
        };
        var body = {
          depotRecord: this.prepareForDatabase(newDepotRecord)
        };
        await this.sendRequest(params, body, "post");
        let isServerUpdated = await this.updateSingleServerProgram(
          newDepotRecord.depot,
          this.masterCompanyMap.get(newDepotRecord.company).region,
          "isActive"
        );
        if (!(isServerUpdated === true)) {
          throw new Error("Unable to update server program. Please try again or re-sync the server!");
        }
        if (isActive) {
          this.$message.success("Successfully enabled depot and updated server program!");
        } else {
          this.$message.success("Successfully disabled depot and updated server program!");
        }
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
      } catch (error) {
        this.$message.error("Error while updating isActive - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        return;
      }
    },

    // =================
    // async customUpdateFunction() {
    //   let
    //   this.depotData.forEach(depotRecord => {
    //        try {

    //     let newDepotRecord = cloneDeep(depotRecord);
    //     newDepotRecord.isActive = isActive;
    //     var params = {
    //       objectType: "DPO",
    //       operation: "update",
    //       region: this.masterCompanyMap.get(newDepotRecord.company).region
    //     };
    //     var body = {
    //       depotRecord: this.prepareForDatabase(newDepotRecord)
    //     };
    //     await this.sendRequest(params, body, "post");
    //     let isServerUpdated = await this.updateSingleServerProgram(
    //       newDepotRecord.depot,
    //       this.masterCompanyMap.get(newDepotRecord.company).region,
    //       "isActive"
    //     );
    //     if (!(isServerUpdated === true)) {
    //       throw new Error("Unable to update server program. Please try again or re-sync the server!");
    //     }
    //     if (isActive) {
    //       this.$message.success("Successfully enabled depot and updated server program!");
    //     } else {
    //       this.$message.success("Successfully disabled depot and updated server program!");
    //     }

    //   } catch (error) {
    //     this.$message.error("Error while updating custom attributes - " + (error.message ? error.message : error));
    //   }
    //     })
    //  this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
    // },
    // =================

    async resyncServer(depotRecord) {
      try {
        let isServerResynced = await this.updateSingleServerProgram(
          depotRecord.depot,
          this.masterCompanyMap.get(depotRecord.company).region,
          "allConfigurationProperties"
        );
        if (!(isServerResynced === true)) {
          this.$message.error("Unable to resync server at this time. Please try again later or contact Support Team.");
        } else {
          this.$message.success("Successfully resynced server!");
        }
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
      } catch (error) {
        this.$message.error("Error while resyncing server - " + (error.message ? error.message : error));
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
      }
    },

    showUpdateIsActiveConfirm(depotRecord, isActive) {
      let content;
      if (isActive) {
        content = "Chargers from this depot will now be added to the charger list on Charger Status Monitor page";
      } else {
        content = "Chargers from this depot will no longer be added to the charger list on Charger Status Monitor page";
      }
      this.$confirm({
        title: "Are you sure you want to " + (isActive ? "enable " : "disable ") + depotRecord.depot + "?",
        content: content,
        okText: "Yes",
        okType: "danger",
        cancelText: "No",
        onOk: () => {
          return this.updateIsActive(depotRecord, isActive);
        }
      });
    },

    showResyncConfirm(depotRecord) {
      let content =
        "NOTE - Resyncing the server will not re-initialize the AWS client; server must be restarted for that.";
      this.$confirm({
        title: "Confirm server resync for " + depotRecord.depot + "?",
        content: content,
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          return this.resyncServer(depotRecord);
        }
      });
    },

    showAllowUnrestrictedChargingConfirm() {
      let content =
        "NOTE - Selecting this will disregard all present charging restrictions and allow charging 24/7 for all the selected depots.";
      this.$confirm({
        title: "Allow Unrestricted Charging for Selected Depots?",
        content: content,
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          return this.handleGroupUnrestrictedCharging(true);
        }
      });
    },

    showRemoveUnrestrictedChargingConfirm() {
      let content =
        "Selecting this will remove unrestricted charging for the selected depots and kick in the present charging restrictions. NOTE - This change will not go through for depots who do not have any other charging restrictions present.";
      this.$confirm({
        title: "Remove Unrestricted Charging for Selected Depots?",
        content: content,
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          return this.handleGroupUnrestrictedCharging(false);
        }
      });
    },

    async handleGroupUnrestrictedCharging(isChargingOn) {
      if (isChargingOn) {
        let selectedData = cloneDeep(this.selectedRows);
        try {
          let promiseArray = [];
          let depotNames = [];
          let region = this.masterCompanyMap.get(this.selectedRows[0].company).region;
          selectedData.forEach(depot => {
            let currentTimeSlots = JSON.parse(depot.timeSlots);
            if (!this.checkZeroTimeSlot(currentTimeSlots)) {
              currentTimeSlots.push({
                noChargingStartTime: "00:00",
                noChargingStopTime: "00:00",
                isWeekendExcluded: false,
                isOvernight: false
              });
              depot.timeSlots = JSON.stringify(currentTimeSlots);
              let params = {
                objectType: "DPO",
                operation: "update",
                region: region
              };
              var body = {
                depotRecord: this.prepareForDatabase(depot)
              };
              promiseArray.push(this.sendRequest(params, body, "post"));
              depotNames.push(depot.depot);
            }
          });
          // only proceed to send API requests and update server program if required for any selected depot.
          if (promiseArray.length) {
            await Promise.all(promiseArray);
            let returnedDepots = await this.updateMultipleServerPrograms(depotNames, region, "timeSlots");
            if (!returnedDepots.length) {
              this.$message.success(
                "Successfully allowed unrestricted charging to selected depots and updated server programs!"
              );
            } else {
              this.$error({
                title: "Error Updating Server Program(s)",
                content:
                  "Unable to update server program for the following depot(s): " +
                  returnedDepots.join(", ") +
                  ". Please try again or re-sync the server!"
              });
            }
            this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
          } else {
            this.$message.warning("Selected depots already have unrestricted charging.");
          }
        } catch (error) {
          this.$message.error(
            "Error while adding unrestricted charging to selected depots - " + (error.message ? error.message : error)
          );
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        }
      } else {
        let selectedData = cloneDeep(this.selectedRows);
        try {
          let promiseArray = [];
          let depotNames = [];
          let region = this.masterCompanyMap.get(this.selectedRows[0].company).region;
          selectedData.forEach(depot => {
            let currentTimeSlots = JSON.parse(depot.timeSlots);
            if (this.checkZeroTimeSlot(currentTimeSlots) && currentTimeSlots.length > 1) {
              currentTimeSlots = currentTimeSlots.filter(timeSlot => {
                return timeSlot.noChargingStartTime != "00:00" && timeSlot.noChargingStopTime != "00:00";
              });
              depot.timeSlots = JSON.stringify(currentTimeSlots);
              let params = {
                objectType: "DPO",
                operation: "update",
                region: region
              };
              var body = {
                depotRecord: this.prepareForDatabase(depot)
              };
              promiseArray.push(this.sendRequest(params, body, "post"));
              depotNames.push(depot.depot);
            }
          });
          // only proceed to send API requests and update server program if required for any selected depot.
          if (promiseArray.length) {
            await Promise.all(promiseArray);
            let returnedDepots = await this.updateMultipleServerPrograms(depotNames, region, "timeSlots");
            if (!returnedDepots.length) {
              this.$message.success(
                "Successfully removing unrestricted charging from selected depots and updated server programs!"
              );
            } else {
              this.$error({
                title: "Error Updating Server Program(s)",
                content:
                  "Unable to update server program for the following depot(s): " +
                  returnedDepots.join(", ") +
                  ". Please try again or re-sync the server!"
              });
            }
            this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
          } else {
            this.$message.warning(
              "Selected depots do not have unrestricted charging or have only one charging restriction."
            );
          }
        } catch (error) {
          this.$message.error(
            "Error while removing unrestricted charging from selected depots - " +
              (error.message ? error.message : error)
          );
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        }
      }
    },

    async handleGroupAddTimeSlot(fieldValues) {
      let selectedData = cloneDeep(this.selectedRows);
      try {
        this.addTimeSlotFormButtonLoading = true;
        let promiseArray = [];
        let depotNames = [];
        let region = this.masterCompanyMap.get(this.selectedRows[0].company).region;
        selectedData.forEach(depot => {
          let currentTimeSlots = JSON.parse(depot.timeSlots);
          let addedTimeSlot = {
            noChargingStartTime: fieldValues.noChargingStartTime.format("HH:mm").toString(),
            noChargingStopTime: fieldValues.noChargingStopTime.format("HH:mm").toString(),
            isWeekendExcluded: fieldValues.isWeekendExcluded === "true",
            isOvernight: fieldValues.isOvernight === "true"
          };
          if (!this.checkSpecificTimeSlot(addedTimeSlot, currentTimeSlots)) {
            currentTimeSlots.push(addedTimeSlot);
            depot.timeSlots = JSON.stringify(currentTimeSlots);
            let params = {
              objectType: "DPO",
              operation: "update",
              region: region
            };
            var body = {
              depotRecord: this.prepareForDatabase(depot)
            };
            promiseArray.push(this.sendRequest(params, body, "post"));
            depotNames.push(depot.depot);
          }
        });
        // only proceed to send API requests and update server program if required for any selected depot.
        if (promiseArray.length) {
          await Promise.all(promiseArray);
          let returnedDepots = await this.updateMultipleServerPrograms(depotNames, region, "timeSlots");
          if (!returnedDepots.length) {
            this.$message.success(
              "Successfully added no charging time slot to selected depots and updated server programs!"
            );
          } else {
            this.$error({
              title: "Error Updating Server Program(s)",
              content:
                "Unable to update server program for the following depot(s): " +
                returnedDepots.join(", ") +
                ". Please try again or re-sync the server!"
            });
          }
          this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
        } else {
          this.$message.warning("Selected depots already have the added time slot.");
        }
        this.addTimeSlotFormButtonLoading = false;
        this.addTimeSlotModalVisible = false;
      } catch (error) {
        this.$message.error(
          "Error while adding no charging time slot to selected depots - " + (error.message ? error.message : error)
        );
        this.addTimeSlotFormButtonLoading = false;
        this.addTimeSlotModalVisible = false;
        this.loadDepotData(this.depotSearchForm.getFieldValue("company"));
      }
    },

    handleGroupActionClick(clickEvent) {
      switch (clickEvent.key) {
        case "moveChargers":
          this.handleMoveChargers();
          break;
        case "allowUnrestrictedCharging":
          this.showAllowUnrestrictedChargingConfirm();
          break;
        case "removeUnrestrictedCharging":
          this.showRemoveUnrestrictedChargingConfirm();
          break;
        case "addTimeSlot":
          this.addTimeSlotModalVisible = true;
          break;
        default:
          break;
      }
    },

    customCreateDepotFormValidator(rule, value, callback) {
      if (value) {
        try {
          let jsonList = JSON.parse(value);
          switch (rule.field) {
            case "chargerList": {
              let macIDList = [],
                chargerNumberList = [],
                priorityList = [];
              jsonList.forEach(chargerRecord => {
                if (!chargerRecord.macID || !chargerRecord.chargerNumber || !chargerRecord.priority) {
                  throw new Error("One of the required attributes missing.");
                } else if (chargerRecord.macID.length != 12) {
                  throw new Error(
                    "MAC ID for charger number " + chargerRecord.chargerNumber + " must be 12 characters."
                  );
                } else {
                  if (macIDList.includes(chargerRecord.macID)) {
                    callback("Duplicate MAC ID found: " + chargerRecord.macID + ".");
                  } else {
                    macIDList.push(chargerRecord.macID);
                  }
                  if (chargerNumberList.includes(chargerRecord.chargerNumber)) {
                    callback("Duplicate Charger Number found: " + chargerRecord.chargerNumber + ".");
                  } else {
                    chargerNumberList.push(chargerRecord.chargerNumber);
                  }
                  if (priorityList.includes(chargerRecord.priority)) {
                    callback("Duplicate priority found: " + chargerRecord.priority + ".");
                  } else {
                    priorityList.push(chargerRecord.priority);
                  }
                }
              });
              callback();
              break;
            }
            case "userList": {
              jsonList.forEach(userRecord => {
                if (!userRecord.username || !userRecord.pin) {
                  throw new Error("One of the required attributes missing.");
                } else if (userRecord.pin.length != 4) {
                  throw new Error("Pin for username " + userRecord.username + " must be 4 characters.");
                } else {
                  if (!userRecord.username.match(/^[A-Za-z]+$/)) {
                    throw new Error("Username can only contain alphabets.");
                  }
                }
              });
              callback();
              break;
            }
            case "timeSlots": {
              jsonList.forEach(timeSlot => {
                if (
                  !timeSlot.noChargingStopTime ||
                  !timeSlot.noChargingStartTime ||
                  timeSlot.isOvernight == null ||
                  timeSlot.isWeekendExcluded == null
                ) {
                  throw new Error("One of the required attributes missing.");
                }
              });
              callback();
              break;
            }
          }
        } catch (error) {
          switch (error.name) {
            case "SyntaxError":
              callback("Invalid JSON Format. Error: " + error.message);
              break;
            case "TypeError":
              callback("Invalid input. Error: Input must be a JSON array.");
              break;
            default:
              callback("Error: " + error.message);
              break;
          }
        }
      } else {
        callback();
      }
    },

    customAddFormValidator(rule, value, callback) {
      if (value) {
        try {
          let jsonList = JSON.parse(value);
          jsonList.forEach(chargerRecord => {
            if (!chargerRecord.macID || !chargerRecord.chargerNumber || !chargerRecord.priority) {
              throw new Error("One of the required attributes missing.");
            } else if (chargerRecord.macID.length != 12) {
              throw new Error("MAC ID for charger number " + chargerRecord.chargerNumber + " must be 12 characters.");
            } else {
              let currentChargerNumber, currentMacID;
              if (
                this.addChargerFormInitialValues.chargerList.some(currentCharger => {
                  currentChargerNumber = currentCharger.chargerNumber;
                  return currentCharger.macID === chargerRecord.macID;
                })
              ) {
                throw new Error(
                  "Duplicate MAC ID for new charger number " +
                    chargerRecord.chargerNumber +
                    " and current charger number " +
                    currentChargerNumber +
                    "."
                );
              } else if (
                this.addChargerFormInitialValues.chargerList.some(currentCharger => {
                  currentMacID = currentCharger.macID;
                  return currentCharger.chargerNumber === chargerRecord.chargerNumber;
                })
              ) {
                throw new Error(
                  "Duplicate charger numbers for new MAC ID " +
                    chargerRecord.macID +
                    " and current MAC ID " +
                    currentMacID +
                    "."
                );
              } else if (
                this.addChargerFormInitialValues.chargerList.some(currentCharger => {
                  currentChargerNumber = currentCharger.chargerNumber;
                  return currentCharger.priority === chargerRecord.priority;
                })
              ) {
                throw new Error(
                  "Duplicate priority for new charger number " +
                    chargerRecord.chargerNumber +
                    " and current charger number " +
                    currentChargerNumber +
                    "."
                );
              }
            }
          });
          callback();
        } catch (error) {
          switch (error.name) {
            case "SyntaxError":
              callback("Invalid JSON Format. Error: " + error.message);
              break;
            case "TypeError":
              callback("Invalid input. Error: Input must be a JSON array.");
              break;
            default:
              callback("Error: " + error.message);
              break;
          }
        }
      } else {
        callback("Charger List cannot be empty!");
      }
    },

    customDepotNameValidator(rule, value, callback) {
      if (value) {
        if (
          this.depotData.some(depot => {
            return depot.depot === value;
          })
        ) {
          callback("Depot with this name already exists.");
        }
        callback();
      } else {
        callback();
      }
    },

    customFleetListValidator(rule, value, callback) {
      if (value) {
        if (value.length > 1) {
          callback("Only one fleet per depot is allowed during this transition phase.");
        }
        callback();
      } else {
        callback();
      }
    },

    customEditFormValidator(rule, value, callback) {
      switch (rule.field) {
        case "chargerNumber":
          if (value) {
            let currentMacID;
            if (
              this.editChargerFormInitialValues.chargerList.some(currentCharger => {
                currentMacID = currentCharger.macID;
                return (
                  currentCharger.chargerNumber === value &&
                  currentCharger.key != this.editChargerFormInitialValues.selectedChargerKey
                );
              })
            ) {
              callback("Duplicate charger number - Currently assigned to MAC ID " + currentMacID + ".");
            }
          } else {
            callback("Please enter a charger number!");
          }
          callback();
          break;
        case "priority":
          if (value) {
            let currentChargerNumber;
            if (
              this.editChargerFormInitialValues.chargerList.some(currentCharger => {
                currentChargerNumber = currentCharger.chargerNumber;
                return (
                  currentCharger.priority === value &&
                  currentCharger.key != this.editChargerFormInitialValues.selectedChargerKey
                );
              })
            ) {
              callback("Duplicate priority - Currently assigned to charger number " + currentChargerNumber + ".");
            }
          } else {
            callback("Please enter a priority!");
          }
          callback();
          break;
        case "macID":
          if (value) {
            if (value.length != 12) {
              callback("MAC ID must be exactly 12 characters.");
            } else {
              let currentChargerNumber;
              if (
                this.editChargerFormInitialValues.chargerList.some(currentCharger => {
                  currentChargerNumber = currentCharger.chargerNumber;
                  return (
                    currentCharger.macID === value &&
                    currentCharger.key != this.editChargerFormInitialValues.selectedChargerKey
                  );
                })
              ) {
                callback("Duplicate MAC ID - Currently under charger number " + currentChargerNumber + ".");
              }
            }
          } else {
            callback("Please enter a MAC ID!");
          }
          callback();
          break;
      }
    },

    customMoveChargersValidator(targetKeys) {
      try {
        // separate the target and source charger lists and check if any duplicate records are present.
        let targetChargerList = this.moveChargersData.filter(chargerRecord => targetKeys.includes(chargerRecord.key));
        let sourceChargerList = this.moveChargersData.filter(chargerRecord => !targetKeys.includes(chargerRecord.key));
        let chargerNumberList = [],
          priorityList = [];
        targetChargerList.forEach(chargerRecord => {
          if (chargerNumberList.includes(chargerRecord.chargerNumber)) {
            throw new Error(
              "Duplicate Charger Number found in " +
                this.moveChargersTitles[1] +
                ": " +
                chargerRecord.chargerNumber +
                "."
            );
          } else {
            chargerNumberList.push(chargerRecord.chargerNumber);
          }
          if (priorityList.includes(chargerRecord.priority)) {
            throw new Error(
              "Duplicate priority found in " + this.moveChargersTitles[1] + ": " + chargerRecord.priority + "."
            );
          } else {
            priorityList.push(chargerRecord.priority);
          }
        });
        chargerNumberList = [];
        priorityList = [];
        sourceChargerList.forEach(chargerRecord => {
          if (chargerNumberList.includes(chargerRecord.chargerNumber)) {
            throw new Error(
              "Duplicate Charger Number found in " +
                this.moveChargersTitles[0] +
                ": " +
                chargerRecord.chargerNumber +
                "."
            );
          } else {
            chargerNumberList.push(chargerRecord.chargerNumber);
          }
          if (priorityList.includes(chargerRecord.priority)) {
            throw new Error(
              "Duplicate priority found in " + this.moveChargersTitles[0] + ": " + chargerRecord.priority + "."
            );
          } else {
            priorityList.push(chargerRecord.priority);
          }
        });
        // if no errors are thrown, simply set the value of the transferTable component.
        this.moveChargersForm.setFields({
          transferTable: {
            value: targetKeys
          }
        });
      } catch (errorMessage) {
        // set the value of the transferTable component along with the error message.
        this.moveChargersForm.setFields({
          transferTable: {
            value: targetKeys,
            errors: [errorMessage]
          }
        });
      }
    },

    handleUploadChange(info) {
      let fileList = [...info.fileList];
      // check the status of the file upload.
      if (info.file.status === "done") {
        this.$message.success(`${info.file.name} file uploaded successfully.`);
        // once file is uploaded successfully, parse the content and convert to JSON.
        let reader = new FileReader();
        reader.onload = e => {
          try {
            const csv2json = require("csvjson-csv2json");
            const json = csv2json(e.target.result, { parseNumbers: true });
            if (this.addChargerModalVisible) {
              this.addChargerForm.setFieldsValue({ chargerList: JSON.stringify(json) });
            } else {
              this.createDepotForm.setFieldsValue({ chargerList: JSON.stringify(json) });
            }
          } catch (error) {
            if (this.addChargerModalVisible) {
              this.addChargerForm.setFieldsValue({ chargerList: "Invalid CSV Format" });
            } else {
              this.createDepotForm.setFieldsValue({ chargerList: "Invalid CSV Format" });
            }
          }
        };
        reader.readAsText(info.file.originFileObj);
      } else if (info.file.status === "error") {
        this.$message.error(`${info.file.name} file upload failed.`);
      }

      // disable upload button after one upload.
      if (fileList.length != 0) {
        this.uploadCSVButtonDisabled = true;
      } else {
        this.uploadCSVButtonDisabled = false;
        if (this.addChargerModalVisible) {
          this.addChargerForm.resetFields(["chargerList"]);
        } else {
          this.createDepotForm.resetFields(["chargerList"]);
        }
      }

      this.uploadFileList = fileList;
    },

    handleConfigurationUpload(info) {
      let fileList = [...info.fileList];
      // check the status of the file upload.
      if (info.file.status === "done") {
        this.$message.success(`${info.file.name} file uploaded successfully.`);
        // once file is uploaded successfully, parse the content of the JSON config object and fill the form values.
        let reader = new FileReader();
        reader.onload = e => {
          try {
            let configObject = JSON.parse(e.target.result);
            this.createDepotForm.setFieldsValue({ depot: configObject.depot });
            if (this.displayCompanies.includes(configObject.company)) {
              this.createDepotForm.setFieldsValue({ company: configObject.company });
            }
            if (configObject.company) {
              this.fleetList = this.masterCompanyMap.get(configObject.company).fleets;
            } else {
              this.fleetList = [];
            }
            this.createDepotForm.setFieldsValue({ maxConcurrentChargers: configObject.maxConcurrentChargers });
            this.createDepotForm.setFieldsValue({ maxChargingCurrent: configObject.maxChargingCurrent });
            this.createDepotForm.setFieldsValue({
              overriddenChargerCount: configObject.overriddenChargerCount.toString()
            });
            this.createDepotForm.setFieldsValue({ timeoutForReadyDisconnect: configObject.timeoutForReadyDisconnect });
            this.createDepotForm.setFieldsValue({
              timeoutForChargingDisconnect: configObject.timeoutForChargingDisconnect
            });
            this.createDepotForm.setFieldsValue({ privateKeyFile: configObject.privateKeyFile });
            this.createDepotForm.setFieldsValue({ certificateFile: configObject.certificateFile });
            this.createDepotForm.setFieldsValue({ clientEndpoint: configObject.clientEndpoint });
            this.createDepotForm.setFieldsValue({
              minimumSocRequiredForPauseCommand: configObject.minimumSocRequiredForPauseCommand
            });
            this.createDepotForm.setFieldsValue({ SOCThresholdHigh: configObject.SOCThresholdHigh });
            this.createDepotForm.setFieldsValue({ SOCThresholdMedian: configObject.SOCThresholdMedian });
            this.createDepotForm.setFieldsValue({ utcHoursOffset: configObject.utcHoursOffset });
            this.createDepotForm.setFieldsValue({ maxSocToCharge: configObject.MaxSocToCharge });
            this.createDepotForm.setFieldsValue({
              chargerList: JSON.stringify(configObject.chargerList)
            });
            this.createDepotForm.setFieldsValue({
              userList: JSON.stringify(configObject.userList)
            });
            this.createDepotForm.setFieldsValue({
              timeSlots: JSON.stringify(configObject.timeSlots)
            });
          } catch (error) {
            this.$message.error("Error parsing JSON - " + error);
          }
        };
        reader.readAsText(info.file.originFileObj);
      } else if (info.file.status === "error") {
        this.$message.error(`${info.file.name} file upload failed.`);
      }

      // disable upload button after one upload.
      if (fileList.length != 0) {
        this.uploadTXTButtonDisabled = true;
      } else {
        this.uploadTXTButtonDisabled = false;
        this.createDepotForm.resetFields();
      }

      this.configUploadFileList = fileList;
    },

    // function to remove all the locally added attributes from a depotRecord and update the revision & updatedBy attributes.
    prepareForDatabase(depotRecord) {
      delete depotRecord.key;
      delete depotRecord.numberOfChargers;
      delete depotRecord.syncStatus;
      depotRecord.chargerList.forEach(chargerRecord => {
        if (chargerRecord.key) {
          delete chargerRecord.key;
        }
        chargerRecord.revision = new Date(chargerRecord.revision).toISOString();
      });
      depotRecord.chargerList = JSON.stringify(depotRecord.chargerList);
      depotRecord.revision = new Date();
      depotRecord.updatedBy = this.UserName;

      // hotfix for allDayChargingAvailable bug on server program side.
      let currentTimeSlots = JSON.parse(depotRecord.timeSlots);
      let zeroTimeSlots = currentTimeSlots.filter(timeSlot => {
        return timeSlot.noChargingStartTime == "00:00" && timeSlot.noChargingStopTime == "00:00";
      });
      if (zeroTimeSlots.length) {
        currentTimeSlots = currentTimeSlots = currentTimeSlots.filter(timeSlot => {
          return timeSlot.noChargingStartTime != "00:00" && timeSlot.noChargingStopTime != "00:00";
        });
        currentTimeSlots.push({
          noChargingStartTime: "00:00",
          noChargingStopTime: "00:00",
          isWeekendExcluded: false,
          isOvernight: false
        });
        depotRecord.timeSlots = JSON.stringify(currentTimeSlots);
      }
      return JSON.stringify(depotRecord);
    },

    dummyUploadRequest({ onSuccess }) {
      setTimeout(() => {
        onSuccess("Upload Successful");
      }, 0);
    },

    setTotalNumberOfChargers(totalChargerCount) {
      this.totalNumberOfChargers = totalChargerCount;
    },

    saveUserData(userData) {
      this.userData = userData;
    },

    onSelectChange(selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys;
      this.selectedRows = selectedRows;
    },

    handleMoveChargers() {
      let targetKeys = [];
      let leftDepot = cloneDeep(this.selectedRows[0]);
      leftDepot.chargerList.forEach(chargerRecord => {
        chargerRecord.key = chargerRecord.entityId;
        chargerRecord.title = chargerRecord.macID;
      });
      let rightDepot = cloneDeep(this.selectedRows[1]);
      rightDepot.chargerList.forEach(chargerRecord => {
        chargerRecord.key = chargerRecord.entityId;
        chargerRecord.title = chargerRecord.macID;
        targetKeys.push(chargerRecord.entityId);
      });
      this.moveChargersData = leftDepot.chargerList.concat(rightDepot.chargerList);
      this.moveChargersCacheData = cloneDeep(this.moveChargersData);
      this.moveChargersTitles = [leftDepot.depot, rightDepot.depot];
      this.moveChargersTargetKeys = targetKeys;
      this.moveChargersForm.getFieldDecorator("transferTable", {
        valuePropName: "targetKeys"
      });
      this.moveChargersForm.setFieldsValue({ transferTable: targetKeys });
      this.moveChargersModalVisible = true;
    },

    handleMoveChargersChange(nextTargetKeys) {
      this.moveChargersTargetKeys = nextTargetKeys;
      this.customMoveChargersValidator(nextTargetKeys);
    },

    getRowSelection({ selectedKeys, itemSelectAll, itemSelect }) {
      return {
        onSelectAll(selected, selectedRows) {
          const treeSelectedKeys = selectedRows.filter(item => !item.disabled).map(({ key }) => key);
          const diffKeys = selected
            ? difference(treeSelectedKeys, selectedKeys)
            : difference(selectedKeys, treeSelectedKeys);
          itemSelectAll(diffKeys, selected);
        },
        onSelect({ key }, selected) {
          itemSelect(key, selected);
        },
        selectedRowKeys: selectedKeys,
        fixed: true
      };
    },

    editChargerRecord(key) {
      const newData = [...this.moveChargersData];
      const target = newData.filter(item => key === item.key)[0];
      this.editingKey = key;
      if (target) {
        target.editable = true;
        this.moveChargersData = newData;
      }
    },

    handleChargerRecordChange(value, key, columnName) {
      if (!value || !Number.isInteger(value)) {
        this.$message.warning("Value must be a valid integer!");
      } else {
        const newData = [...this.moveChargersData];
        const target = newData.filter(item => key === item.key)[0];
        if (target) {
          target[columnName] = value;
          this.moveChargersData = newData;
        }
      }
    },

    handleCompanySelectChange(selectedCompany) {
      if (selectedCompany) {
        this.fleetList = this.masterCompanyMap.get(selectedCompany).fleets;
      } else {
        this.fleetList = [];
      }
    },

    saveChargerRecord(key) {
      const newData = [...this.moveChargersData];
      const newCacheData = [...this.moveChargersCacheData];
      const target = newData.filter(item => key === item.key)[0];
      const targetCache = newCacheData.filter(item => key === item.key)[0];
      if (target && targetCache) {
        delete target.editable;
        target.revision = new Date();
        this.moveChargersData = newData;
        Object.assign(targetCache, target);
        this.moveChargersCacheData = newCacheData;
      }
      this.editingKey = "";
      this.customMoveChargersValidator(this.moveChargersTargetKeys);
    },

    cancelChargerRecordChange(key) {
      const newData = [...this.moveChargersData];
      const target = newData.filter(item => key === item.key)[0];
      this.editingKey = "";
      if (target) {
        Object.assign(target, this.moveChargersCacheData.filter(item => key === item.key)[0]);
        delete target.editable;
        this.moveChargersData = newData;
      }
    },

    getConnectionTimeout(dataSize) {
      if (dataSize === 1) {
        return 2500;
      } else if (dataSize <= 5) {
        return 5000;
      } else if (dataSize <= 30) {
        return 10000;
      } else {
        return 12500;
      }
    },

    createParamsUrl(paramsObject) {
      var paramsUrl = "";
      var counter = 0;
      var totalCount = Object.keys(paramsObject).length;
      for (var key in paramsObject) {
        if (paramsObject.hasOwnProperty(key)) {
          paramsUrl += key + "=" + paramsObject[key];
          if (counter != totalCount - 1) {
            paramsUrl += "&";
          }
          counter += 1;
        }
      }
      return paramsUrl.replace("#", "%23");
    },

    checkZeroTimeSlot(data) {
      let zeroTimeSlotFound = false;
      if (data.length) {
        data.forEach(timeSlot => {
          if (timeSlot.noChargingStartTime == "00:00" && timeSlot.noChargingStopTime == "00:00") {
            zeroTimeSlotFound = true;
            return;
          }
        });
        return zeroTimeSlotFound;
      }
      return zeroTimeSlotFound;
    },

    checkSpecificTimeSlot(timeSlot, data) {
      return (
        data.filter(
          currentTimeSlot =>
            currentTimeSlot.noChargingStartTime == timeSlot.noChargingStartTime &&
            currentTimeSlot.noChargingStopTime == timeSlot.noChargingStopTime &&
            currentTimeSlot.isOvernight == timeSlot.isOvernight &&
            currentTimeSlot.isWeekendExcluded == timeSlot.isWeekendExcluded
        ).length > 0
      );
    },

    handleCheckboxChange(event) {
      this.allowUnrestrictedCharging = event.target.checked;
      if (this.allowUnrestrictedCharging && !this.checkZeroTimeSlot(this.chargingScheduleDataSource)) {
        let currentDataSource = [...this.chargingScheduleDataSource];
        currentDataSource.push({
          noChargingStartTime: "00:00",
          noChargingStopTime: "00:00",
          isWeekendExcluded: false,
          isOvernight: false,
          rowKey: currentDataSource.length + 1
        });
        this.chargingScheduleDataSource = currentDataSource;
      } else if (!this.allowUnrestrictedCharging && this.checkZeroTimeSlot(this.chargingScheduleDataSource)) {
        const currentDataSource = [...this.chargingScheduleDataSource];
        this.chargingScheduleDataSource = currentDataSource.filter(timeSlot => {
          return timeSlot.noChargingStartTime != "00:00" && timeSlot.noChargingStopTime != "00:00";
        });
      }
    }
  }
};
</script>

<style>
.ant-form-item {
  margin-bottom: 5px;
}

.p {
  margin: 0;
}

.a-button {
  margin: 0 auto;
  display: block;
}

#addTimeSlot {
  z-index: 2000;
  position: relative;
}

#addUser {
  z-index: 2000;
  position: relative;
}

#backModal {
  z-index: 1000;
  position: relative;
}
</style>
